import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Track refresh state to prevent concurrent refresh calls
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies with requests
});

// Add auth token and tenant context to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('simpliview_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // TENANT ISOLATION: Inject tenant_id from active context
  // Priority: 1. impersonated_tenant_id (sysadmin viewing as tenant)
  //           2. user's actual tenant_id (normal users)
  const impersonatedTenantId = localStorage.getItem('impersonated_tenant_id');
  const userTenantId = localStorage.getItem('simpliview_user_tenant_id');
  const activeTenantId = impersonatedTenantId || userTenantId;
  
  // Skip tenant filtering for billing admin endpoints (sysadmins need to see all data)
  const isBillingAdminEndpoint = config.url?.includes('/billing/invoices') || 
                                  config.url?.includes('/billing/clients') ||
                                  config.url?.includes('/billing/tenants');
  
  if (activeTenantId && !isBillingAdminEndpoint) {
    // Add tenant_id header for backend filtering
    config.headers['X-Tenant-ID'] = activeTenantId;
    
    // Also inject as query param for GET requests (for routes that check params)
    if (config.method === 'get' && !config.params?.tenant_id) {
      config.params = config.params || {};
      config.params.tenant_id = activeTenantId;
    }
    
    // For POST/PUT/PATCH, we can optionally inject into body
    // BUT only for non-FormData requests
    if (['post', 'put', 'patch'].includes(config.method) && 
        config.data && 
        typeof config.data === 'object' && 
        !(config.data instanceof FormData)) {
      // Don't overwrite if already set
      if (!config.data.tenant_id) {
        config.data.tenant_id = activeTenantId;
      }
    }
  }
  
  return config;
});

// Response interceptor with silent refresh support
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Don't retry if:
    // - Not a 401 error
    // - Already retried this request
    // - This is the refresh endpoint itself
    // - This is the login endpoint
    if (
      error.response?.status !== 401 ||
      originalRequest._retry ||
      originalRequest.url?.includes('/auth/refresh') ||
      originalRequest.url?.includes('/auth/login')
    ) {
      return Promise.reject(error);
    }

    // Mark this request as retried
    originalRequest._retry = true;

    // If we're already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        })
        .catch(err => {
          return Promise.reject(err);
        });
    }

    isRefreshing = true;

    try {
      // Attempt silent refresh
      const response = await axios.post(
        `${API_BASE}/auth/refresh`,
        {},
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );

      const { token, access_token_expires_at } = response.data;

      // Update stored token
      localStorage.setItem('simpliview_token', token);
      localStorage.setItem('simpliview_token_expiry', access_token_expires_at);

      // Update default authorization header
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      originalRequest.headers.Authorization = `Bearer ${token}`;

      // Process queued requests
      processQueue(null, token);

      console.log('[ApiClient] Token refreshed successfully');
      
      // Retry the original request
      return apiClient(originalRequest);
    } catch (refreshError) {
      // Refresh failed - clear queue and trigger re-auth
      processQueue(refreshError, null);

      const errorDetail = refreshError.response?.data?.detail || 'session_expired';
      console.warn('[ApiClient] Token refresh failed:', errorDetail);

      // Dispatch custom event for AuthContext to show re-auth modal
      // This is a fallback mechanism if the refresh in AuthContext also failed
      window.dispatchEvent(new CustomEvent('auth:session-expired', { 
        detail: { reason: errorDetail }
      }));

      // Don't clear localStorage here - let the ReauthModal handle the flow
      // The user might successfully re-authenticate

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export const api = {
  // Authentication
  login: (credentials) => apiClient.post('/auth/login', credentials),
  logout: () => apiClient.post('/auth/logout'),
  getCurrentUser: () => apiClient.get('/auth/me'),
  updateProfile: (data) => apiClient.put('/auth/profile', data),
  forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (data) => apiClient.post('/auth/reset-password', data),
  changePassword: (data) => apiClient.post('/auth/change-password', data),
  
  // Admin - User Management
  resendUserInvite: (userId) => apiClient.post(`/admin/users/${userId}/resend-invite`),

  // Filters
  getFilters: () => apiClient.get('/filters'),
  getHierarchyFilterOptions: (params = {}) => apiClient.get('/hierarchy/filter-options', { params }),
  getHierarchyFilter: (params = {}) => apiClient.get('/hierarchy/filter', { params }),
  
  // Company Health
  getCompanyHealth: () => apiClient.get('/company-health'),
  
  // Persona Dashboards
  getExecutiveDashboard: () => apiClient.get('/dashboard/executive'),
  getExecutiveDashboardV2: (filters = {}) => {
    const params = {};
    if (filters.portfolio_ids?.length) params.portfolio_ids = filters.portfolio_ids.join(',');
    if (filters.program_ids?.length) params.program_ids = filters.program_ids.join(',');
    if (filters.project_ids?.length) params.project_ids = filters.project_ids.join(',');
    if (filters.statuses?.length) params.statuses = filters.statuses.join(',');
    if (filters.release_ids?.length) params.release_ids = filters.release_ids.join(',');
    if (filters.sprint_ids?.length) params.sprint_ids = filters.sprint_ids.join(',');
    if (filters.themes?.length) params.themes = filters.themes.join(',');
    if (filters.date_range) params.date_range = filters.date_range;
    return apiClient.get('/dashboard/executive-v2', { params });
  },
  getPortfolioManagerDashboard: (filters = {}) => {
    const params = {};
    if (filters.portfolio_ids?.length) params.portfolio_id = filters.portfolio_ids[0];
    if (filters.program_ids?.length) params.program_ids = filters.program_ids.join(',');
    if (filters.project_ids?.length) params.project_ids = filters.project_ids.join(',');
    if (filters.pm_ids?.length) params.pm_ids = filters.pm_ids.join(',');
    if (filters.release_ids?.length) params.release_ids = filters.release_ids.join(',');
    if (filters.statuses?.length) params.statuses = filters.statuses.join(',');
    if (filters.health_categories?.length) params.health_categories = filters.health_categories.join(',');
    if (filters.confidence_min !== undefined && filters.confidence_min > 0) params.confidence_min = filters.confidence_min;
    if (filters.confidence_max !== undefined && filters.confidence_max < 100) params.confidence_max = filters.confidence_max;
    return apiClient.get('/dashboard/portfolio-manager', { params });
  },
  getProgramManagerDashboard: (programId) => 
    apiClient.get('/dashboard/program-manager', { params: { program_id: programId } }),
  getProjectManagerDashboard: (projectId) => 
    apiClient.get('/dashboard/project-manager', { params: { project_id: projectId } }),
  getProjectManagerDashboardV2: (filters = {}) => {
    const params = {};
    if (filters.project_ids?.length) params.project_ids = filters.project_ids.join(',');
    if (filters.program_ids?.length) params.program_ids = filters.program_ids.join(',');
    if (filters.portfolio_ids?.length) params.portfolio_id = filters.portfolio_ids[0];
    if (filters.pm_ids?.length) params.pm_ids = filters.pm_ids.join(',');
    if (filters.sprint_ids?.length) params.sprint_ids = filters.sprint_ids.join(',');
    if (filters.release_ids?.length) params.release_ids = filters.release_ids.join(',');
    if (filters.statuses?.length) params.statuses = filters.statuses.join(',');
    if (filters.health_categories?.length) params.health_categories = filters.health_categories.join(',');
    if (filters.confidence_min !== undefined && filters.confidence_min > 0) params.confidence_min = filters.confidence_min;
    if (filters.confidence_max !== undefined && filters.confidence_max < 100) params.confidence_max = filters.confidence_max;
    if (filters.work_item_types?.length) params.work_item_types = filters.work_item_types.join(',');
    return apiClient.get('/dashboard/project-manager-v2', { params });
  },
  getUserPreferences: () => apiClient.get('/dashboard/user-preferences'),
  saveUserPreferences: (prefs) => apiClient.post('/dashboard/user-preferences', prefs),
  
  // Filter Presets
  getFilterPresets: (params = {}) => apiClient.get('/dashboard/filter-presets', { params }),
  createFilterPreset: (presetData) => apiClient.post('/dashboard/filter-presets', presetData),
  updateFilterPreset: (presetId, presetData) => apiClient.put(`/dashboard/filter-presets/${presetId}`, presetData),
  deleteFilterPreset: (presetId) => apiClient.delete(`/dashboard/filter-presets/${presetId}`),
  
  // Roadmap (Gantt) - Legacy endpoints
  getPortfolioRoadmap: (portfolioId, depth = 3) => 
    apiClient.get('/roadmap/portfolio', { params: { portfolio_id: portfolioId, depth } }),
  getProgramRoadmap: (programId) => apiClient.get(`/roadmap/program/${programId}`),
  getProjectRoadmap: (projectId) => apiClient.get(`/roadmap/project/${projectId}`),
  getEpicRoadmap: (epicId) => apiClient.get(`/roadmap/epic/${epicId}`),
  
  // Roadmap - View-specific 4-layer hierarchy endpoints (NEW)
  getExecutiveRoadmap: (params = {}) => {
    const queryParams = {};
    if (params.portfolio_id) queryParams.portfolio_id = params.portfolio_id;
    if (params.program_id) queryParams.program_id = params.program_id;
    return apiClient.get('/roadmap/view/executive', { params: queryParams });
  },
  getPMORoadmap: (params = {}) => {
    const queryParams = {};
    if (params.portfolio_id) queryParams.portfolio_id = params.portfolio_id;
    if (params.program_id) queryParams.program_id = params.program_id;
    if (params.project_id) queryParams.project_id = params.project_id;
    return apiClient.get('/roadmap/view/pmo', { params: queryParams });
  },
  getPMRoadmap: (params = {}) => {
    const queryParams = {};
    if (params.project_id) queryParams.project_id = params.project_id;
    if (params.epic_id) queryParams.epic_id = params.epic_id;
    return apiClient.get('/roadmap/view/pm', { params: queryParams });
  },
  
  // Portfolio
  getPortfolios: () => apiClient.get('/portfolio'),
  getPortfolioDashboard: (portfolioId, params) => 
    apiClient.get(`/portfolio/${portfolioId}`, { params }),
  
  // Programs
  getPrograms: (params = {}) => apiClient.get('/programs', { params }),
  getProgramDashboard: (programId, params) => 
    apiClient.get(`/program/${programId}`, { params }),
  
  // Projects
  getProjects: (params = {}) => apiClient.get('/projects', { params }),
  getProject: (tenantId, projectId) => apiClient.get(`/projects/${projectId}`, { params: { tenant_id: tenantId } }),
  getProjectDashboard: (projectId, params) => 
    apiClient.get(`/project/${projectId}`, { params }),
  
  // Project Versioning (Upload Center)
  getProjectHierarchy: () => apiClient.get('/projects/hierarchy/portfolios'),
  getProjectVersions: (projectId) => apiClient.get(`/projects/${projectId}/versions`),
  uploadProjectVersion: (projectId, formData) => 
    apiClient.post(`/projects/${projectId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  activateVersion: (projectId, versionNo) => 
    apiClient.post(`/projects/${projectId}/versions/${versionNo}/activate`),
  getVersionDetails: (projectId, versionNo) => 
    apiClient.get(`/projects/${projectId}/versions/${versionNo}`),
  createProject: (data) => apiClient.post('/projects', data),
  updateProject: (projectId, data) => apiClient.patch(`/projects/${projectId}`, data),
  deleteProject: (projectId, hardDelete = false) => 
    apiClient.delete(`/projects/${projectId}`, { params: { hard_delete: hardDelete } }),
  
  // Epics
  getEpics: (params = {}) => apiClient.get('/epics', { params }),
  getEpicDetails: (id) => apiClient.get(`/epics/${id}`),
  createEpic: (data) => apiClient.post('/workitems/epics', data),
  updateEpic: (id, data) => apiClient.put(`/workitems/epics/${id}`, data),
  deleteEpic: (id, hardDelete = false) => apiClient.delete(`/workitems/epics/${id}`, { params: { hard_delete: hardDelete } }),
  
  // Features
  getFeatures: (params = {}) => apiClient.get('/features', { params }),
  getFeatureDetails: (id) => apiClient.get(`/features/${id}`),
  createFeature: (data) => apiClient.post('/workitems/features', data),
  updateFeature: (id, data) => apiClient.put(`/workitems/features/${id}`, data),
  deleteFeature: (id, hardDelete = false) => apiClient.delete(`/workitems/features/${id}`, { params: { hard_delete: hardDelete } }),
  
  // Stories
  getStories: (params = {}) => apiClient.get('/stories', { params }),
  createStory: (data) => apiClient.post('/workitems/stories', data),
  
  // Tasks
  getTasks: (params = {}) => apiClient.get('/tasks', { params }),
  createTask: (data) => apiClient.post('/workitems/tasks', data),
  
  // Work Items (Generic)
  updateWorkItem: (id, data) => apiClient.put(`/workitems/workitem/${id}`, data),
  deleteWorkItem: (id, hardDelete = false) => apiClient.delete(`/workitems/workitem/${id}`, { params: { hard_delete: hardDelete } }),
  bulkUpdateWorkItems: (data) => apiClient.post('/workitems/bulk-update', data),
  canEditWorkItems: (tenantId) => apiClient.get('/workitems/can-edit', { params: { tenant_id: tenantId } }),
  
  // Rollup Engine
  getRollup: (entityType, entityId) => apiClient.get(`/rollup/${entityType}/${entityId}`),
  getRollupWhy: (entityType, entityId) => apiClient.get(`/rollup/${entityType}/${entityId}/why`),
  validateRollupUpdate: (data) => apiClient.post('/rollup/validate-update', data),
  recomputeRollup: (entityType, entityId, propagate = true) => 
    apiClient.post(`/rollup/recompute/${entityType}/${entityId}`, null, { params: { propagate } }),
  hasChildren: (entityType, entityId) => apiClient.get(`/rollup/has-children/${entityType}/${entityId}`),
  
  // Issues
  getIssues: (params = {}) => apiClient.get('/issues', { params }),
  
  // Dependencies
  getDependencies: (params = {}) => apiClient.get('/dependencies', { params }),
  
  // Decisions
  getDecisions: (params = {}) => apiClient.get('/decisions', { params }),
  
  // ==========================================
  // Tracking Filter Bar 2.0 APIs
  // ==========================================
  
  // Issues Filter 2.0
  filterIssues: (params = {}) => apiClient.get('/tracking/issues/filter', { params }),
  getIssuesFilterOptions: (params = {}) => apiClient.get('/tracking/issues/filter-options', { params }),
  
  // Dependencies Filter 2.0
  filterDependencies: (params = {}) => apiClient.get('/tracking/dependencies/filter', { params }),
  getDependenciesFilterOptions: (params = {}) => apiClient.get('/tracking/dependencies/filter-options', { params }),
  
  // Decisions Filter 2.0
  filterDecisions: (params = {}) => apiClient.get('/tracking/decisions/filter', { params }),
  getDecisionsFilterOptions: (params = {}) => apiClient.get('/tracking/decisions/filter-options', { params }),
  
  // ==========================================
  // Filter Presets (P2)
  // ==========================================
  
  // User Presets (Personal + Shared visible)
  getUserPresets: (userId, tenantId, pageType = null) => {
    const params = { user_id: userId, tenant_id: tenantId };
    if (pageType) params.page_type = pageType;
    return apiClient.get('/users/presets', { params });
  },
  createUserPreset: (preset, userId, userName, tenantId) => 
    apiClient.post('/users/presets', preset, { 
      params: { user_id: userId, user_name: userName, tenant_id: tenantId } 
    }),
  updateUserPreset: (presetId, update, userId) => 
    apiClient.put(`/users/presets/${presetId}`, update, { params: { user_id: userId } }),
  deleteUserPreset: (presetId, userId) => 
    apiClient.delete(`/users/presets/${presetId}`, { params: { user_id: userId } }),
  
  // Org Presets (Shared only)
  getOrgPresets: (tenantId, pageType = null) => {
    const params = { tenant_id: tenantId };
    if (pageType) params.page_type = pageType;
    return apiClient.get('/org/presets', { params });
  },
  createOrgPreset: (preset, userId, userName, tenantId) => 
    apiClient.post('/org/presets', preset, { 
      params: { user_id: userId, user_name: userName, tenant_id: tenantId } 
    }),
  
  // Tier Info
  getPresetTierInfo: (tenantId, userId) => 
    apiClient.get('/presets/tier-info', { params: { tenant_id: tenantId, user_id: userId } }),

  // ==========================================
  // Global Search (P3)
  // ==========================================
  globalSearch: (query, types = null, limit = 20) => {
    const params = { q: query, limit };
    if (types) params.types = types;
    return apiClient.get('/search', { params });
  },
  getSearchSuggestions: (query, limit = 10) => 
    apiClient.get('/search/suggestions', { params: { q: query, limit } }),
  getSearchNavigation: (type, id) => 
    apiClient.get('/search/navigate', { params: { type, id } }),

  // ==========================================
  // Auto-Sync (SharePoint/Teams) - P4
  // ==========================================
  
  // Capabilities
  getSyncCapabilities: (tenantId) => 
    apiClient.get('/autosync/capabilities', { params: { tenant_id: tenantId } }),
  
  // Sync Configurations
  listSyncConfigs: (tenantId, projectId = null, programId = null) => {
    const params = { tenant_id: tenantId };
    if (projectId) params.project_id = projectId;
    if (programId) params.program_id = programId;
    return apiClient.get('/autosync/configs', { params });
  },
  createSyncConfig: (config, tenantId, userId) => 
    apiClient.post('/autosync/configs', config, { 
      params: { tenant_id: tenantId, user_id: userId } 
    }),
  updateSyncConfig: (configId, update, tenantId, userId) => 
    apiClient.put(`/autosync/configs/${configId}`, update, { 
      params: { tenant_id: tenantId, user_id: userId } 
    }),
  deleteSyncConfig: (configId, tenantId) => 
    apiClient.delete(`/autosync/configs/${configId}`, { 
      params: { tenant_id: tenantId } 
    }),
  triggerSyncNow: (configId, tenantId, userId) => 
    apiClient.post(`/autosync/configs/${configId}/sync-now`, {}, { 
      params: { tenant_id: tenantId, user_id: userId } 
    }),
  
  // Sync Logs
  getSyncLogs: (configId, tenantId, limit = 5) => 
    apiClient.get(`/autosync/configs/${configId}/logs`, { 
      params: { tenant_id: tenantId, limit } 
    }),
  
  // Admin (Enterprise only)
  getAdminSyncOverview: (tenantId) => 
    apiClient.get('/autosync/admin/overview', { params: { tenant_id: tenantId } }),
  adminSyncAll: (tenantId, userId) => 
    apiClient.post('/autosync/admin/sync-all', {}, { 
      params: { tenant_id: tenantId, user_id: userId } 
    }),
  adminForceRollup: (tenantId, userId) => 
    apiClient.post('/autosync/admin/force-rollup', {}, { 
      params: { tenant_id: tenantId, user_id: userId } 
    }),

  // ==========================================
  // Hierarchy Configuration
  // ==========================================
  getHierarchyConfig: (tenantId) => 
    apiClient.get(`/tenants/${tenantId}/hierarchy-config`),
  updateHierarchyConfig: (tenantId, config) => 
    apiClient.put(`/tenants/${tenantId}/hierarchy-config`, config, {
      params: { user_id: 'admin' }  // Will be replaced with actual user
    }),
  getHierarchyLabels: (tenantId) => 
    apiClient.get('/hierarchy/labels', { params: { tenant_id: tenantId } }),

  createDecision: (data) => apiClient.post('/decisions', data),
  getSteercoQueue: (params = {}) => apiClient.get('/decisions/steerco-queue', { params }),
  submitDecision: (decisionId, data) => apiClient.post(`/decisions/${decisionId}/submit`, data),
  escalateDecision: (decisionId, data) => apiClient.post(`/decisions/${decisionId}/escalate`, data),
  approveDecision: (decisionId, data) => apiClient.post(`/decisions/${decisionId}/approve`, data),
  rejectDecision: (decisionId, data) => apiClient.post(`/decisions/${decisionId}/reject`, data),
  requestDecisionInfo: (decisionId, data) => apiClient.post(`/decisions/${decisionId}/request-info`, data),
  getDecisionComments: (decisionId) => apiClient.get(`/decisions/${decisionId}/comments`),
  addDecisionComment: (decisionId, data) => apiClient.post(`/decisions/${decisionId}/comments`, data),

  // ===========================
  // Governance CRUD (Issues, Decisions, Dependencies)
  // ===========================
  
  // Issues & Risks CRUD
  createIssue: (data) => apiClient.post('/governance/issues', data),
  updateIssue: (issueId, data) => apiClient.patch(`/governance/issues/${issueId}`, data),
  deleteIssue: (issueId) => apiClient.delete(`/governance/issues/${issueId}`),
  getIssue: (issueId) => apiClient.get(`/governance/issues/${issueId}`),
  listIssues: (params = {}) => apiClient.get('/governance/issues', { params }),
  
  // Decisions CRUD  
  createDecisionNew: (data) => apiClient.post('/governance/decisions', data),
  updateDecision: (decisionId, data) => apiClient.patch(`/governance/decisions/${decisionId}`, data),
  deleteDecision: (decisionId) => apiClient.delete(`/governance/decisions/${decisionId}`),
  getDecision: (decisionId) => apiClient.get(`/governance/decisions/${decisionId}`),
  listDecisions: (params = {}) => apiClient.get('/governance/decisions', { params }),
  approveDecisionNew: (decisionId, data) => apiClient.post(`/governance/decisions/${decisionId}/approve`, data),
  rejectDecisionNew: (decisionId, data) => apiClient.post(`/governance/decisions/${decisionId}/reject`, data),
  
  // Dependencies CRUD (Polymorphic)
  createDependency: (data) => apiClient.post('/governance/dependencies', data),
  updateDependency: (depId, data) => apiClient.patch(`/governance/dependencies/${depId}`, data),
  deleteDependency: (depId) => apiClient.delete(`/governance/dependencies/${depId}`),
  getDependency: (depId) => apiClient.get(`/governance/dependencies/${depId}`),
  listDependencies: (params = {}) => apiClient.get('/governance/dependencies', { params }),
  
  // Entity Picker / Lookup
  getEntities: (params = {}) => apiClient.get('/governance/entities', { params }),
  getEntityMetrics: (entityType, entityId) => apiClient.get(`/governance/metrics/${entityType}/${entityId}`),
  
  // Governance Rollup
  getEntityRollup: (entityType, entityId) => apiClient.get(`/governance/rollup/${entityType}/${entityId}`),
  getRollupSummary: (entityType = null, entityId = null) => {
    const params = {};
    if (entityType) params.entity_type = entityType;
    if (entityId) params.entity_id = entityId;
    return apiClient.get('/governance/rollup-summary', { params });
  },
  
  // Bulk Operations - Issues
  bulkDeleteIssues: (ids) => apiClient.post('/governance/issues/bulk/delete', { ids }),
  bulkUpdateIssuesStatus: (ids, status) => apiClient.post('/governance/issues/bulk/status', { ids, status }),
  bulkUpdateIssuesPriority: (ids, priority) => apiClient.post('/governance/issues/bulk/priority', { ids, priority }),
  
  // Bulk Operations - Decisions
  bulkDeleteDecisions: (ids) => apiClient.post('/governance/decisions/bulk/delete', { ids }),
  bulkUpdateDecisionsStatus: (ids, status) => apiClient.post('/governance/decisions/bulk/status', { ids, status }),
  
  // Bulk Operations - Dependencies
  bulkDeleteDependencies: (ids) => apiClient.post('/governance/dependencies/bulk/delete', { ids }),
  bulkUpdateDependenciesStatus: (ids, status) => apiClient.post('/governance/dependencies/bulk/status', { ids, status }),

  // Milestones API
  getExecutiveMilestones: (params = {}) => apiClient.get('/milestones/executive', { params }),
  getPMOMilestones: (params = {}) => apiClient.get('/milestones/pmo', { params }),
  getPMOFeatureMilestones: (params = {}) => apiClient.get('/milestones/pmo/features', { params }),
  getPMMilestones: (params = {}) => apiClient.get('/milestones/pm', { params }),
  getMilestonesTimeline: (params = {}) => apiClient.get('/milestones/timeline', { params }),

  // Project People
  getProjectPeople: (projectId) => apiClient.get(`/projects/${projectId}/people`),
  addProjectPerson: (projectId, data) => apiClient.post(`/projects/${projectId}/people`, data),
  removeProjectPerson: (projectId, personId) => apiClient.delete(`/projects/${projectId}/people/${personId}`),
  getPeopleRoles: () => apiClient.get('/people/roles'),

  // Releases
  getReleases: (params = {}) => apiClient.get('/releases', { params }),
  createRelease: (data) => apiClient.post('/releases', data),
  updateRelease: (releaseId, data) => apiClient.put(`/releases/${releaseId}`, data),
  deleteRelease: (releaseId) => apiClient.delete(`/releases/${releaseId}`),

  // Sprints
  getSprints: (params = {}) => apiClient.get('/sprints', { params }),
  createSprint: (data) => apiClient.post('/sprints', data),
  updateSprint: (sprintId, data) => apiClient.put(`/sprints/${sprintId}`, data),
  deleteSprint: (sprintId) => apiClient.delete(`/sprints/${sprintId}`),

  // Work Items Hierarchy Configuration
  getWorkItemsHierarchyConfig: (tenantId) => apiClient.get('/workitems/hierarchy-config', { params: { tenant_id: tenantId } }),
  getTaskParentOptions: (tenantId, projectId) => apiClient.get('/workitems/task-parent-options', { params: { tenant_id: tenantId, project_id: projectId } }),
  reparentTask: (taskId, data) => apiClient.post(`/workitems/reparent-task/${taskId}`, data),
  migrateStories: (data) => apiClient.post('/workitems/migrate-stories', data),
  
  // Work Item Composer (Batch Create Hierarchy)
  createWorkItemHierarchy: (data) => apiClient.post('/workitems/composer', data),
  getProjectWorkHierarchy: (projectId, params = {}) => apiClient.get(`/workitems/project-hierarchy/${projectId}`, { params }),
  inlineUpdateWorkItem: (itemType, itemId, data) => apiClient.patch(`/workitems/inline/${itemType}/${itemId}`, data),
  
  // Drag-and-Drop Re-parenting
  reparentWorkItem: (data) => apiClient.post('/workitems/reparent', data),
  getValidDropTargets: (itemType, itemId, projectId) => apiClient.get(`/workitems/valid-drop-targets/${itemType}/${itemId}`, { params: { project_id: projectId } }),
  reorderWorkItem: (data) => apiClient.post('/workitems/reorder', data),
  
  // Rollups
  getItemRollup: (itemType, itemId) => apiClient.get(`/workitems/rollup/${itemType}/${itemId}`),
  recalculateRollups: (data) => apiClient.post('/workitems/recalculate-rollups', data),
  getDashboardKpis: (itemType, itemId) => apiClient.get(`/workitems/dashboard-kpis/${itemType}/${itemId}`),

  // P7: Work Item Resource Overview
  getWorkItemResourceOverview: (itemId, tenantId) => 
    apiClient.get(`/workitems/resource-overview/${itemId}`, { params: { tenant_id: tenantId } }),
  getProjectResourceRollup: (projectId, tenantId) => 
    apiClient.get(`/workitems/project-resource-rollup/${projectId}`, { params: { tenant_id: tenantId } }),
  updateDeliveryAlignment: (itemId, data) => 
    apiClient.patch(`/workitems/delivery-alignment/${itemId}`, data),
  updateResourceDemand: (itemId, data) => 
    apiClient.patch(`/workitems/resource-demand/${itemId}`, data),

  // P8: Enhanced Upload
  uploadP8Template: (formData, tenantId, options = {}) => 
    apiClient.post(`/p8/upload-unified?tenant_id=${tenantId}&rollback_on_error=${options.rollbackOnError ?? true}&dry_run=${options.dryRun ?? false}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  validateP8Template: (formData, tenantId) => 
    apiClient.post(`/p8/validate?tenant_id=${tenantId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  downloadP8Template: () => 
    apiClient.get('/p8/download-template', { responseType: 'blob' }),
  getP8ImportHistory: (tenantId, limit = 20) => 
    apiClient.get('/p8/import-history', { params: { tenant_id: tenantId, limit } }),

  // P9: Scenario Planning
  createScenario: (data, tenantId, userId) => 
    apiClient.post(`/scenarios?tenant_id=${tenantId}&user_id=${userId || 'system'}`, data),
  listScenarios: (tenantId, status, limit = 50) => 
    apiClient.get('/scenarios', { params: { tenant_id: tenantId, status, limit } }),
  getScenario: (scenarioId, tenantId) => 
    apiClient.get(`/scenarios/${scenarioId}`, { params: { tenant_id: tenantId } }),
  updateScenario: (scenarioId, data, tenantId) => 
    apiClient.patch(`/scenarios/${scenarioId}?tenant_id=${tenantId}`, data),
  deleteScenario: (scenarioId, tenantId) => 
    apiClient.delete(`/scenarios/${scenarioId}?tenant_id=${tenantId}`),
  duplicateScenario: (scenarioId, tenantId, newName) => 
    apiClient.post(`/scenarios/${scenarioId}/duplicate`, null, { params: { tenant_id: tenantId, new_name: newName } }),
  calculateScenario: (scenarioId, tenantId, forceRefresh = false) => 
    apiClient.post(`/scenarios/${scenarioId}/calculate?tenant_id=${tenantId}&force_refresh=${forceRefresh}`),
  compareScenarios: (scenarioIds, tenantId, includeBaseline = true) => 
    apiClient.post(`/scenarios/compare?tenant_id=${tenantId}`, { scenarioIds, includeBaseline }),
  quickSimulate: (data, tenantId) => 
    apiClient.post(`/scenarios/quick-simulate?tenant_id=${tenantId}`, data),
  getScenarioRecommendations: (scenarioId, tenantId) => 
    apiClient.get(`/scenarios/recommendations/${scenarioId}`, { params: { tenant_id: tenantId } }),

  // Audit Trail
  getItemAuditLog: (itemType, itemId, params = {}) => apiClient.get(`/audit/item/${itemType}/${itemId}`, { params }),
  createAuditEntry: (data) => apiClient.post('/audit/log', data),
  getRecentAuditLogs: (params = {}) => apiClient.get('/audit/recent', { params }),

  // Bulk Operations (duplicate removed - see line 325)
  bulkArchiveWorkItems: (data) => apiClient.post('/workitems/bulk-archive', data),

  // Multi-Project Upload
  previewMultiProjectUpload: (formData) => apiClient.post('/projects/multi-upload/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  processMultiProjectUpload: (formData) => apiClient.post('/projects/multi-upload/process', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Teams
  getTeams: (params = {}) => apiClient.get('/teams', { params }),
  getTeamDetails: (id) => apiClient.get(`/teams/${id}`),
  
  // Work Item Details (for hover/drawer)
  getWorkItemDetails: (id) => apiClient.get(`/work-item/${id}`),
  
  // Excel Upload
  uploadExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/upload-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  // Sprint 18: Unified Excel Intake
  uploadUnifiedExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/p8/upload-unified-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  // Upload Status & History
  getUploadStatus: () => apiClient.get('/upload/status'),
  getUploadHistory: (limit = 20) => apiClient.get(`/upload/history?limit=${limit}`),
  getProjectsUploadStatus: () => apiClient.get('/upload/projects-status'),
  
  // Download Template
  downloadTemplate: () => `${BACKEND_URL}/api/download-template`,
  
  // CSV Export
  exportCSV: (type, params = {}) => {
    const queryParams = new URLSearchParams({ export_type: type, ...params }).toString();
    return `${BACKEND_URL}/api/export?${queryParams}`;
  },
  
  // ===== Admin Module APIs =====
  
  // Users
  getAdminUsers: (params = {}) => apiClient.get('/admin/users', { params }),
  createAdminUser: (userData) => apiClient.post('/admin/users', userData),
  updateAdminUser: (userId, userData) => apiClient.patch(`/admin/users/${userId}`, userData),
  deleteAdminUser: (userId) => apiClient.delete(`/admin/users/${userId}`),
  assignUserRole: (userId, roleData) => apiClient.post(`/admin/users/${userId}/roles`, roleData),
  removeUserRole: (userId, roleKey, scopeId) => {
    const params = scopeId ? { scope_id: scopeId } : {};
    return apiClient.delete(`/admin/users/${userId}/roles/${roleKey}`, { params });
  },
  bulkImportUsers: (users) => apiClient.post('/admin/users/bulk-import', users),
  
  // Roles
  getAdminRoles: () => apiClient.get('/admin/roles'),
  getPermissionsMatrix: () => apiClient.get('/admin/roles/permissions-matrix'),
  getRolePermissions: (roleKey) => apiClient.get(`/admin/roles/${roleKey}/permissions`),
  
  // Permission Overrides
  getPermissionOverrides: (params = {}) => apiClient.get('/admin/overrides', { params }),
  createPermissionOverride: (data) => apiClient.post('/admin/overrides', data),
  revokePermissionOverride: (overrideId) => apiClient.delete(`/admin/overrides/${overrideId}`),
  getUserOverrides: (userId) => apiClient.get(`/admin/overrides/user/${userId}`),
  listAllPermissions: () => apiClient.get('/admin/permissions/list'),
  
  // Role Permissions (Configurable)
  getConfigurableMatrix: () => apiClient.get('/admin/roles/configurable-matrix'),
  updateRolePermissions: (roleKey, permissions) => apiClient.put(`/admin/roles/${roleKey}/permissions`, { permissions }),
  resetRolePermissions: (roleKey) => apiClient.post(`/admin/roles/${roleKey}/permissions/reset`),
  
  // Configuration
  getAdminConfig: () => apiClient.get('/admin/config'),
  updateAdminConfig: (configData) => apiClient.put('/admin/config', configData),
  getAdminStatuses: () => apiClient.get('/admin/config/statuses'),
  updateAdminStatuses: (statuses) => apiClient.put('/admin/config/statuses', statuses),
  
  // Brand
  getAdminBrand: () => apiClient.get('/admin/brand'),
  updateAdminBrand: (brandData) => apiClient.put('/admin/brand', brandData),
  
  // Audit
  getAuditLogs: (params = {}) => apiClient.get('/admin/audit', { params }),
  exportAuditLogs: (params = {}) => apiClient.get('/admin/audit/export', { params, responseType: 'blob' }),
  
  // Support
  createSupportTicket: (ticketData) => apiClient.post('/admin/support/ticket', ticketData),
  getSupportTickets: (params = {}) => apiClient.get('/admin/support/tickets', { params }),
  getSystemStatus: () => apiClient.get('/admin/support/system-status'),
  updateSystemStatus: (statusData) => apiClient.put('/admin/support/system-status', statusData),
  
  // Integrations
  getIntegrations: () => apiClient.get('/admin/integrations'),
  updateSSOSettings: (ssoData) => apiClient.put('/admin/integrations/sso', ssoData),
  
  // ===== Reports Module APIs =====
  
  // Report Definitions
  getReports: () => apiClient.get('/reports'),
  runReport: (reportKey, filters = {}) => apiClient.post('/reports/run', { report_key: reportKey, filters }),
  getReportHistory: (params = {}) => apiClient.get('/reports/history', { params }),
  getReportRun: (runId) => apiClient.get(`/reports/runs/${runId}`),
  
  // Report Schedules
  getReportSchedules: (params = {}) => apiClient.get('/reports/schedules', { params }),
  createReportSchedule: (scheduleData) => apiClient.post('/reports/schedules', scheduleData),
  updateReportSchedule: (scheduleId, scheduleData) => apiClient.patch(`/reports/schedules/${scheduleId}`, scheduleData),
  deleteReportSchedule: (scheduleId) => apiClient.delete(`/reports/schedules/${scheduleId}`),
  
  // Report Presets
  getReportPresets: (params = {}) => apiClient.get('/reports/presets', { params }),
  createReportPreset: (presetData) => apiClient.post('/reports/presets', presetData),
  deleteReportPreset: (presetId) => apiClient.delete(`/reports/presets/${presetId}`),
  
  // ===== Billing & Pricing APIs =====
  
  // Pricing Catalog
  getPricingCatalog: (params = {}) => apiClient.get('/billing/catalog', { params }),
  getTierPricing: (currency, tier) => apiClient.get(`/billing/catalog/${currency}/${tier}`),
  upsertPricing: (pricingData) => apiClient.post('/billing/catalog', pricingData),
  deactivatePricing: (currency, tier) => apiClient.delete(`/billing/catalog/${currency}/${tier}`),
  
  // Tenants
  getTenants: (params = {}) => apiClient.get('/billing/tenants', { params }),
  createTenant: (tenantData) => apiClient.post('/billing/tenants', tenantData),
  
  // Tenant Billing
  getTenantBilling: (tenantId) => apiClient.get(`/billing/tenant/${tenantId}`),
  updateTenantBilling: (tenantId, billingData) => apiClient.patch(`/billing/tenant/${tenantId}`, billingData),
  schedulePlanChange: (tenantId, planData) => apiClient.post(`/billing/tenant/${tenantId}/plan-change`, planData),
  getTenantLimits: (tenantId) => apiClient.get(`/billing/tenant/${tenantId}/limits`),
  checkTenantLimit: (tenantId, checkData) => apiClient.post(`/billing/tenant/${tenantId}/check-limit`, checkData),
  getTenantEvents: (tenantId, params = {}) => apiClient.get(`/billing/tenant/${tenantId}/events`, { params }),
  
  // Invoices
  getInvoices: (params = {}) => apiClient.get('/billing/invoices', { params }),
  createInvoice: (invoiceData) => apiClient.post('/billing/invoices', invoiceData),
  createProjectInvoice: (invoiceData) => apiClient.post('/billing/invoices/project', invoiceData),
  getInvoice: (invoiceId) => apiClient.get(`/billing/invoices/${invoiceId}`),
  updateInvoice: (invoiceId, updateData) => apiClient.patch(`/billing/invoices/${invoiceId}`, updateData),
  updateProjectInvoice: (invoiceId, invoiceData) => apiClient.put(`/billing/invoices/${invoiceId}`, invoiceData),
  downloadInvoicePdf: (invoiceId) => apiClient.get(`/billing/invoices/${invoiceId}/pdf`, { responseType: 'blob' }),
  previewInvoiceHtml: (invoiceId) => apiClient.get(`/billing/invoices/${invoiceId}/preview`),
  
  // Client Billing Profiles
  getClients: (params = {}) => apiClient.get('/billing/clients', { params }),
  createClient: (clientData) => apiClient.post('/billing/clients', clientData),
  getClient: (clientId) => apiClient.get(`/billing/clients/${clientId}`),
  updateClient: (clientId, updateData) => apiClient.patch(`/billing/clients/${clientId}`, updateData),
  deleteClient: (clientId) => apiClient.delete(`/billing/clients/${clientId}`),
  
  // Currency Info
  getSupportedCurrencies: () => apiClient.get('/billing/currencies'),
  
  // Tier Defaults
  getTierDefaults: () => apiClient.get('/billing/tier-defaults'),
  
  // ===== Trial Management APIs =====
  getTrialConfig: () => apiClient.get('/billing/trial/config'),
  updateTrialConfig: (config) => apiClient.post('/billing/trial/config', config),
  startTrial: (tenantId, trialData) => apiClient.post(`/billing/tenant/${tenantId}/trial/start`, trialData),
  getTenantTrial: (tenantId) => apiClient.get(`/billing/tenant/${tenantId}/trial`),
  endTrial: (tenantId, endData) => apiClient.post(`/billing/tenant/${tenantId}/trial/end`, endData),
  listTrials: (params = {}) => apiClient.get('/billing/trials', { params }),
  expireTrials: () => apiClient.post('/billing/trials/expire-check'),
  getEffectiveTier: (tenantId) => apiClient.get(`/billing/tenant/${tenantId}/effective-tier`),
  
  // ===== Plan Summary & Feature Gating =====
  getPlanSummary: (tenantId) => apiClient.get(`/billing/tenant/${tenantId}/plan-summary`),
  setTestTenantFlag: (tenantId, isTest) => apiClient.post(`/billing/tenant/${tenantId}/test-flag`, { is_test_tenant: isTest }),
  getInvoiceableTenants: () => apiClient.get('/billing/invoiceable-tenants'),

  // ==========================================
  // Daily Signal™ Notifications
  // ==========================================
  
  // User Preferences
  getNotificationPreferences: () => apiClient.get('/notifications/preferences'),
  saveNotificationPreferences: (prefs) => apiClient.post('/notifications/preferences', prefs),
  sendTestSignal: () => apiClient.post('/notifications/test-signal'),
  
  // Admin: Global Config
  getNotificationConfig: () => apiClient.get('/notifications/admin/config'),
  saveNotificationConfig: (config) => apiClient.post('/notifications/admin/config', config),
  
  // Admin: Teams Config
  getTeamsConfig: () => apiClient.get('/notifications/admin/teams/config'),
  addTeamsWebhook: (webhookData) => apiClient.post('/notifications/admin/teams/webhook', webhookData),
  removeTeamsWebhook: (webhookUrl) => apiClient.delete('/notifications/admin/teams/webhook', { params: { webhook_url: webhookUrl } }),
  testTeamsWebhook: (webhookUrl) => apiClient.post('/notifications/admin/teams/test', { webhook_url: webhookUrl }),
  
  // Admin: Teams Graph API v1.5
  addTeamsGraphTarget: (targetData) => apiClient.post('/notifications/admin/teams/graph-target', targetData),
  getTeamsGraphTargets: () => apiClient.get('/notifications/admin/teams/graph-targets'),
  deleteTeamsGraphTarget: (targetId) => apiClient.delete(`/notifications/admin/teams/graph-target/${targetId}`),
  updateTeamsGraphTarget: (targetId, data) => apiClient.put(`/notifications/admin/teams/graph-target/${targetId}`, data),
  getTeamsGraphStatus: () => apiClient.get('/notifications/admin/teams/graph/status'),
  listAvailableTeams: () => apiClient.get('/notifications/admin/teams/graph/teams'),
  listTeamChannels: (teamId) => apiClient.get(`/notifications/admin/teams/graph/teams/${teamId}/channels`),
  testTeamsGraphMessage: (targetData) => apiClient.post('/notifications/admin/teams/graph/test', targetData),
  
  // Admin: Role Defaults
  getNotificationRoleDefaults: () => apiClient.get('/notifications/admin/role-defaults'),
  saveNotificationRoleDefault: (policy) => apiClient.post('/notifications/admin/role-defaults', policy),
  
  // Admin: User Overrides
  getNotificationUserOverrides: (userId = null) => {
    const params = userId ? { user_id: userId } : {};
    return apiClient.get('/notifications/admin/user-overrides', { params });
  },
  saveNotificationUserOverride: (override) => apiClient.post('/notifications/admin/user-overrides', override),
  deleteNotificationUserOverride: (userId) => apiClient.delete(`/notifications/admin/user-overrides/${userId}`),
  
  // Admin: Outbox
  getNotificationOutbox: (status = null, limit = 50) => {
    const params = { limit };
    if (status) params.status = status;
    return apiClient.get('/notifications/admin/outbox', { params });
  },
  retryNotification: (entryId) => apiClient.post(`/notifications/admin/outbox/${entryId}/retry`),
  
  // Admin: Signal Templates
  getSignalTemplates: (includeSystem = true) => apiClient.get('/notifications/admin/templates', { params: { include_system: includeSystem } }),
  getSignalTemplate: (templateId) => apiClient.get(`/notifications/admin/templates/${templateId}`),
  createSignalTemplate: (template) => apiClient.post('/notifications/admin/templates', template),
  updateSignalTemplate: (templateId, template) => apiClient.put(`/notifications/admin/templates/${templateId}`, template),
  deleteSignalTemplate: (templateId) => apiClient.delete(`/notifications/admin/templates/${templateId}`),
  cloneSignalTemplate: (templateId, newName) => apiClient.post(`/notifications/admin/templates/${templateId}/clone`, { new_name: newName }),
  previewSignalTemplate: (templateId, previewData = {}) => apiClient.post(`/notifications/admin/templates/${templateId}/preview`, previewData),
  getDefaultTemplates: () => apiClient.get('/notifications/admin/templates/defaults/list'),
  
  // Trial Scheduler
  getSchedulerStatus: () => apiClient.get('/billing/scheduler/status'),
  triggerTrialExpireCheck: () => apiClient.post('/billing/scheduler/trigger/expire-check'),
  triggerTrialReminders: () => apiClient.post('/billing/scheduler/trigger/reminders'),
  
  // Comments
  getComments: (params) => apiClient.get('/comments', { params }),
  createComment: (data) => {
    // The backend expects a JSON body with entity_type, entity_id, message, etc.
    // and query params for tenant_id, user_id, user_name
    const body = {
      entity_type: data.entity_type,
      entity_id: data.entity_id,
      message: data.message,
      mentions: data.mentions || [],
      visibility: data.visibility || 'org',
      pinned: data.pinned || false,
      assigned_to: data.assigned_to || null,
      parent_id: data.parent_id || null
    };
    const params = {
      tenant_id: data.tenant_id,
      user_id: data.user_id,
      user_name: data.user_name || 'User',
      user_role: data.user_role || null
    };
    return apiClient.post('/comments', body, { params });
  },
  updateComment: (commentId, data) => apiClient.patch(`/comments/${commentId}`, data.body, { params: data.params }),
  deleteComment: (commentId, params) => apiClient.delete(`/comments/${commentId}`, { params }),
  getPinnedComments: (params) => apiClient.get('/comments/pinned', { params }),
  getCommentCapabilities: (tenantId, userId) => apiClient.get('/comments/capabilities', { params: { tenant_id: tenantId, user_id: userId } }),
  sendCommentNotification: (commentId, params) => apiClient.post(`/comments/${commentId}/notify`, null, { params }),
  
  // Comments Inbox
  getCommentInbox: (params) => apiClient.get('/comments/inbox', { params }),
  getCommentInboxCounts: (params) => apiClient.get('/comments/inbox/counts', { params }),
  updateCommentSettings: (params) => apiClient.put('/comments/settings', null, { params }),
  
  // Comment Subscriptions
  getCommentSubscription: (params) => apiClient.get('/comments/subscriptions', { params }),
  subscribeToComments: (params) => apiClient.post('/comments/subscriptions', null, { params }),
  unsubscribeFromComments: (params) => apiClient.delete('/comments/subscriptions', { params }),
  getUserSubscriptions: (params) => apiClient.get('/comments/subscriptions/list', { params }),
  
  // Notification Preferences (Comments module)
  getCommentNotificationPreferences: (params) => apiClient.get('/comments/notifications/preferences', { params }),
  updateCommentNotificationPreferences: (tenantId, userId, preferences) => 
    apiClient.put('/comments/notifications/preferences', preferences, { 
      params: { tenant_id: tenantId, user_id: userId } 
    }),
  
  // Scoping & Requirements
  getScopingCapabilities: (tenantId) => apiClient.get('/scoping/capabilities', { params: { tenant_id: tenantId } }),
  listScopes: (params) => apiClient.get('/scoping/scopes', { params }),
  getScope: (scopeId, tenantId) => apiClient.get(`/scoping/scopes/${scopeId}`, { params: { tenant_id: tenantId } }),
  createScope: (tenantId, userId, userName, scope) => 
    apiClient.post('/scoping/scopes', scope, { params: { tenant_id: tenantId, user_id: userId, user_name: userName } }),
  updateScope: (scopeId, tenantId, userId, update) => 
    apiClient.patch(`/scoping/scopes/${scopeId}`, update, { params: { tenant_id: tenantId, user_id: userId } }),
  deleteScope: (scopeId, tenantId, userId) => 
    apiClient.delete(`/scoping/scopes/${scopeId}`, { params: { tenant_id: tenantId, user_id: userId } }),
  uploadScopeDocument: (formData) => apiClient.post('/scoping/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  extractScope: (scopeId, tenantId, userId, hierarchyMode) => 
    apiClient.post(`/scoping/scopes/${scopeId}/extract`, null, { 
      params: { tenant_id: tenantId, user_id: userId, hierarchy_mode: hierarchyMode } 
    }),
  structureScope: (scopeId, tenantId, userId, hierarchyMode) => 
    apiClient.post(`/scoping/scopes/${scopeId}/structure`, null, { 
      params: { tenant_id: tenantId, user_id: userId, hierarchy_mode: hierarchyMode } 
    }),
  updateScopeStructure: (scopeId, tenantId, userId, structure) => 
    apiClient.patch(`/scoping/scopes/${scopeId}/structure`, structure, { 
      params: { tenant_id: tenantId, user_id: userId } 
    }),
  listScopingBaselines: (params) => apiClient.get('/scoping/baselines', { params }),
  approveScopingBaseline: (scopeId, tenantId, userId, userName) => 
    apiClient.post(`/scoping/baselines/${scopeId}/approve`, null, { 
      params: { tenant_id: tenantId, user_id: userId, user_name: userName } 
    }),
  rejectScopingBaseline: (scopeId, tenantId, userId, userName, reason) => 
    apiClient.post(`/scoping/baselines/${scopeId}/reject`, null, { 
      params: { tenant_id: tenantId, user_id: userId, user_name: userName, reason } 
    }),
  reviseScopingBaseline: (scopeId, tenantId, userId, userName) => 
    apiClient.post(`/scoping/baselines/${scopeId}/revise`, null, { 
      params: { tenant_id: tenantId, user_id: userId, user_name: userName } 
    }),
  getApprovalStatus: (scopeId, tenantId) => 
    apiClient.get(`/scoping/baselines/${scopeId}/approval-status`, { 
      params: { tenant_id: tenantId } 
    }),
  getApprovalSettings: (tenantId) => 
    apiClient.get('/scoping/config/approval-settings', { params: { tenant_id: tenantId } }),
  updateApprovalSettings: (tenantId, settings) => 
    apiClient.put('/scoping/config/approval-settings', settings, { params: { tenant_id: tenantId } }),
  listScopeDiffs: (params) => apiClient.get('/scoping/diffs', { params }),
  compareScopingBaselines: (tenantId, baselineFromId, baselineToId) => 
    apiClient.post('/scoping/diffs/compare', null, { 
      params: { tenant_id: tenantId, baseline_from_id: baselineFromId, baseline_to_id: baselineToId } 
    }),
  getScopingProjects: (tenantId) => apiClient.get('/scoping/projects', { params: { tenant_id: tenantId } }),
  
  // Create a new project for scoping - uses the main projects endpoint
  createScopingProject: (tenantId, projectData) => 
    apiClient.post('/projects', {
      name: projectData.name,
      description: projectData.description || '',
      tenant_id: tenantId,
      status: projectData.status || 'active',
      hierarchy_mode: projectData.hierarchy_mode || 'agile'
    }),
  aiScopingAssistant: (tenantId, userId, request) => 
    apiClient.post('/scoping/assistant', request, { params: { tenant_id: tenantId, user_id: userId } }),
  generateWorkitemsFromScope: (scopeId, tenantId, userId, userName) => 
    apiClient.post(`/scoping/scopes/${scopeId}/generate-workitems`, null, { 
      params: { tenant_id: tenantId, user_id: userId, user_name: userName } 
    }),
  exportScopeExcel: (scopeId, tenantId) => 
    apiClient.get(`/scoping/scopes/${scopeId}/export/excel`, { 
      params: { tenant_id: tenantId },
      responseType: 'blob'
    }),
  getScopeHealth: (tenantId, days = 7) => 
    apiClient.get('/scoping/health', { params: { tenant_id: tenantId, days } }),
  getScopeCoverage: (tenantId) => 
    apiClient.get('/scoping/coverage', { params: { tenant_id: tenantId } }),
  getScopeCoverageTrends: (tenantId, rangeDays = 7, groupBy = 'portfolio') => 
    apiClient.get('/scoping/coverage/trends', { 
      params: { tenant_id: tenantId, range_days: rangeDays, group_by: groupBy } 
    }),
  checkExistingWorkitems: (tenantId, projectId) => 
    apiClient.get('/scoping/check-existing-workitems', { params: { tenant_id: tenantId, project_id: projectId } }),
  
  // Project Creation (used by Scoping Workspace)
  createProjectForScoping: (projectData) => 
    apiClient.post('/projects', projectData),
  
  // ==========================================
  // SUPPORT AI
  // ==========================================
  
  // Get user-accessible projects for Support AI
  getSupportProjects: (tenantId, userId) => 
    apiClient.get('/support/projects', { params: { tenant_id: tenantId, user_id: userId, scope: 'mine' } }),
  
  // Main Support AI assist endpoint
  supportAssist: (tenantId, userId, request) => 
    apiClient.post('/support/assist', request, { params: { tenant_id: tenantId, user_id: userId } }),
  
  // User preferences for Support AI
  getSupportPreferences: (tenantId, userId) => 
    apiClient.get('/support/user-preferences', { params: { tenant_id: tenantId, user_id: userId } }),
  
  updateSupportPreferences: (tenantId, userId, preferences) => 
    apiClient.post('/support/user-preferences', null, { 
      params: { 
        tenant_id: tenantId, 
        user_id: userId,
        preferred_role: preferences.preferred_role,
        last_project_id: preferences.last_project_id
      } 
    }),
  
  // Explain This Screen - P1
  explainScreen: (tenantId, userId, page, userRole) => 
    apiClient.post('/support/explain-screen', null, { 
      params: { tenant_id: tenantId, user_id: userId, page, user_role: userRole } 
    }),
  
  // Guided Flows - P1
  getGuidedFlow: (flowType, tenantId, userId) => 
    apiClient.get(`/support/guided-flow/${flowType}`, { params: { tenant_id: tenantId, user_id: userId } }),
  
  // Diagnostics - P1.5
  runDiagnostics: (tenantId, userId, scope = 'tenant') => 
    apiClient.get('/support/diagnostics', { params: { tenant_id: tenantId, user_id: userId, scope } }),
  
  // Validators
  validateUploadTemplate: (tenantId, columnHeaders) => 
    apiClient.post('/support/validators/upload-template', { column_headers: columnHeaders }, { params: { tenant_id: tenantId } }),
  
  validateTeamsCredentials: (tenantId, credentials) => 
    apiClient.post('/support/validators/teams-credentials', credentials, { params: { tenant_id: tenantId } }),
  
  validateWhatsAppCredentials: (tenantId, credentials) => 
    apiClient.post('/support/validators/whatsapp-credentials', credentials, { params: { tenant_id: tenantId } }),
  
  validateSharePointConnection: (tenantId, settings) => 
    apiClient.post('/support/validators/sharepoint-connection', settings, { params: { tenant_id: tenantId } }),
  
  // ==========================================
  // PRODUCT MODE (ScopeOps Scoping)
  // ==========================================
  
  getProductConfig: (tenantId) => 
    apiClient.get('/admin/product-config', { params: { tenant_id: tenantId } }),
  
  updateProductConfig: (tenantId, config) => 
    apiClient.post('/admin/product-config', config, { params: { tenant_id: tenantId } }),
  
  getScopingRoles: () => 
    apiClient.get('/admin/scoping-roles'),
  
  getScopingNav: (tenantId) => 
    apiClient.get('/admin/scoping-nav', { params: { tenant_id: tenantId } }),
  
  // ==========================================
  // SCOPING GOVERNANCE (Phase 1)
  // ==========================================
  
  // Decisions ↔ Scope
  getScopeDecisions: (scopeId, tenantId) =>
    apiClient.get(`/scoping/governance/scope/${scopeId}/decisions`, { params: { tenant_id: tenantId } }),
  
  linkDecisionToScope: (decisionId, scopeIds, impactType, impactNotes, tenantId, userId) =>
    apiClient.post(`/scoping/governance/decisions/${decisionId}/link-scope`, scopeIds, {
      params: { impact_type: impactType, impact_notes: impactNotes, tenant_id: tenantId, user_id: userId }
    }),
  
  unlinkDecisionFromScope: (decisionId, scopeId, tenantId) =>
    apiClient.delete(`/scoping/governance/decisions/${decisionId}/unlink-scope/${scopeId}`, {
      params: { tenant_id: tenantId }
    }),
  
  proposeBaselineFromDecision: (decisionId, projectId, tenantId, userId) =>
    apiClient.post(`/scoping/governance/decisions/${decisionId}/propose-baseline`, null, {
      params: { project_id: projectId, tenant_id: tenantId, user_id: userId }
    }),
  
  // Scope Drift
  getScopeDrift: (projectId, baseline, tenantId) =>
    apiClient.get('/scoping/governance/drift', {
      params: { project_id: projectId, baseline: baseline, tenant_id: tenantId }
    }),
  
  getDriftSummary: (projectId, tenantId) =>
    apiClient.get('/scoping/governance/drift/summary', {
      params: { project_id: projectId, tenant_id: tenantId }
    }),
  
  // Risks ↔ Scope
  getScopeRisks: (scopeId, tenantId) =>
    apiClient.get(`/scoping/governance/scope/${scopeId}/risks`, { params: { tenant_id: tenantId } }),
  
  linkRiskToScope: (riskId, scopeIds, tenantId) =>
    apiClient.post(`/scoping/governance/risks/${riskId}/link-scope`, scopeIds, {
      params: { tenant_id: tenantId }
    }),
  
  unlinkRiskFromScope: (riskId, scopeId) =>
    apiClient.delete(`/scoping/governance/risks/${riskId}/unlink-scope/${scopeId}`),
  
  // Category & Checklist
  updateScopeCategory: (scopeId, category, tenantId) =>
    apiClient.put(`/scoping/governance/scope/${scopeId}/category`, null, {
      params: { category: category, tenant_id: tenantId }
    }),
  
  getScopeChecklist: (scopeId, tenantId) =>
    apiClient.get(`/scoping/governance/scope/${scopeId}/checklist`, { params: { tenant_id: tenantId } }),
  
  updateScopeChecklist: (scopeId, checklist, tenantId) =>
    apiClient.put(`/scoping/governance/scope/${scopeId}/checklist`, checklist, {
      params: { tenant_id: tenantId }
    }),
  
  // Documents
  getScopeDocuments: (scopeId, tenantId) =>
    apiClient.get(`/scoping/governance/scope/${scopeId}/docs`, { params: { tenant_id: tenantId } }),
  
  attachDocumentToScope: (scopeId, doc, tenantId) =>
    apiClient.post(`/scoping/governance/scope/${scopeId}/docs`, doc, { params: { tenant_id: tenantId } }),
  
  removeDocumentFromScope: (scopeId, docId, tenantId) =>
    apiClient.delete(`/scoping/governance/scope/${scopeId}/docs/${docId}`, { params: { tenant_id: tenantId } }),
  
  // Aggregate governance data
  getScopeGovernance: (scopeId, tenantId) =>
    apiClient.get(`/scoping/governance/scope/${scopeId}`, { params: { tenant_id: tenantId } }),
  
  // ==========================================
  // SCOPING GOVERNANCE PHASE 2
  // ==========================================
  
  // Document Versioning
  getVersionedDocuments: (scopeId, tenantId, page = 1, limit = 20) =>
    apiClient.get(`/scoping/governance/v2/scope/${scopeId}/docs/versioned`, {
      params: { tenant_id: tenantId, page, limit }
    }),
  
  uploadDocumentVersion: (scopeId, { docId, name, url, type, notes, sizeBytes, replace }, tenantId, userId, userName) =>
    apiClient.post(`/scoping/governance/v2/scope/${scopeId}/docs/version`, null, {
      params: {
        doc_id: docId,
        name,
        url,
        type: type || 'url',
        notes,
        size_bytes: sizeBytes,
        replace: replace || false,
        tenant_id: tenantId,
        user_id: userId,
        user_name: userName
      }
    }),
  
  getDocumentVersions: (scopeId, docId, tenantId) =>
    apiClient.get(`/scoping/governance/v2/scope/${scopeId}/docs/${docId}/versions`, {
      params: { tenant_id: tenantId }
    }),
  
  // Sign-offs
  createSignoff: (signoffData, tenantId, userId, userName) =>
    apiClient.post('/scoping/governance/v2/signoffs', signoffData, {
      params: { tenant_id: tenantId, user_id: userId, user_name: userName }
    }),
  
  getSignoffs: (tenantId, { scopeId, docId, docVersion } = {}) =>
    apiClient.get('/scoping/governance/v2/signoffs', {
      params: { tenant_id: tenantId, scope_id: scopeId, doc_id: docId, doc_version: docVersion }
    }),
  
  // E-Sign
  requestEsign: (esignData, tenantId, userId, userName) =>
    apiClient.post('/scoping/governance/v2/esign/request', esignData, {
      params: { tenant_id: tenantId, user_id: userId, user_name: userName }
    }),
  
  // For testing: simulate webhook
  simulateEsignWebhook: (provider, envelopeId, status) =>
    apiClient.post(`/scoping/governance/v2/esign/webhook/${provider}`, {
      envelope_id: envelopeId,
      status
    }),
  
  // Smart Suggestions
  getSmartSuggestion: (decisionId, tenantId) =>
    apiClient.post('/scoping/governance/v2/smart-suggest', { decision_id: decisionId }, {
      params: { tenant_id: tenantId }
    }),
  
  acceptSmartSuggestion: (decisionId, projectId, tenantId, userId, userName) =>
    apiClient.post('/scoping/governance/v2/smart-suggest/accept', null, {
      params: {
        decision_id: decisionId,
        project_id: projectId,
        tenant_id: tenantId,
        user_id: userId,
        user_name: userName
      }
    }),
  
  getPendingSuggestions: (tenantId, projectId) =>
    apiClient.get('/scoping/governance/v2/smart-suggest/pending', {
      params: { tenant_id: tenantId, project_id: projectId }
    }),
  
  // ==========================================
  // SERVICES REQUEST
  // ==========================================
  
  submitServiceRequest: (request) =>
    apiClient.post('/services/request', request),
  
  getServiceRequests: (tenantId, status = null) =>
    apiClient.get('/services/requests', { params: { tenant_id: tenantId, status } }),
  
  getServiceRequest: (requestId) =>
    apiClient.get(`/services/request/${requestId}`),
  
  // ==========================================
  // PARTNER TENANT MODE
  // ==========================================
  
  getPartnerPortfolio: (partnerUserId, partnerTenantId) =>
    apiClient.get('/partner/portfolio', {
      params: { partner_user_id: partnerUserId, partner_tenant_id: partnerTenantId }
    }),
  
  exportGovernanceBundle: (request, partnerUserId, partnerTenantId, userName = null) =>
    apiClient.post('/partner/export', request, {
      params: { 
        partner_user_id: partnerUserId, 
        partner_tenant_id: partnerTenantId,
        user_name: userName
      }
    }),
  
  // ==========================================
  // GOVERNANCE RELATIONSHIPS LINKING
  // ==========================================
  
  createGovernanceLinks: (request, tenantId, userId, userName = null) =>
    apiClient.post('/governance/links', request, {
      params: { tenant_id: tenantId, user_id: userId, user_name: userName }
    }),
  
  deleteGovernanceLinks: (request, tenantId, userId) =>
    apiClient.delete('/governance/links', {
      data: request,
      params: { tenant_id: tenantId, user_id: userId }
    }),
  
  getGovernanceByWork: (workId, tenantId, includeChildren = false) =>
    apiClient.get(`/governance/by-work/${workId}`, {
      params: { tenant_id: tenantId, include_children: includeChildren }
    }),
  
  getGovernanceByArtifact: (artifactType, artifactId, tenantId) =>
    apiClient.get(`/governance/by-artifact/${artifactType}/${artifactId}`, {
      params: { tenant_id: tenantId }
    }),
  
  getGovernanceRollup: (scope, scopeId, tenantId, window = 30) =>
    apiClient.get('/governance/rollup', {
      params: { scope, scope_id: scopeId, tenant_id: tenantId, window }
    }),
  
  rebuildGovernanceIndex: (tenantId, userId) =>
    apiClient.post('/governance/admin/rebuild-index', null, {
      params: { tenant_id: tenantId, user_id: userId }
    }),
  
  backfillGovernanceLinks: (tenantId, userId) =>
    apiClient.post('/governance/admin/backfill', null, {
      params: { tenant_id: tenantId, user_id: userId }
    }),
  
  // ==========================================
  // AI ScopeOps APIs
  // ==========================================
  
  // Acceptance Criteria
  suggestAcceptanceCriteria: (tenantId, data) =>
    apiClient.post('/ai/acceptance/suggest', data, {
      params: { tenant_id: tenantId }
    }),
  
  saveAcceptanceCriteria: (projectId, tenantId, criteria) =>
    apiClient.post('/ai/acceptance', criteria, {
      params: { project_id: projectId, tenant_id: tenantId }
    }),
  
  // KPIs
  suggestKPIs: (tenantId, data) =>
    apiClient.post('/ai/kpis/suggest', data, {
      params: { tenant_id: tenantId }
    }),
  
  saveKPIs: (projectId, tenantId, kpis) =>
    apiClient.post('/ai/kpis', kpis, {
      params: { project_id: projectId, tenant_id: tenantId }
    }),
  
  // Benefits
  suggestBenefits: (tenantId, data) =>
    apiClient.post('/ai/benefits/suggest', data, {
      params: { tenant_id: tenantId }
    }),
  
  saveBenefits: (projectId, tenantId, benefits) =>
    apiClient.post('/ai/benefits', benefits, {
      params: { project_id: projectId, tenant_id: tenantId }
    }),
  
  linkBenefitToDecision: (decisionId, tenantId, links) =>
    apiClient.post('/ai/benefits/link', links, {
      params: { decision_id: decisionId, tenant_id: tenantId }
    }),
  
  linkKPIToDecision: (decisionId, tenantId, links) =>
    apiClient.post('/ai/kpis/link', links, {
      params: { decision_id: decisionId, tenant_id: tenantId }
    }),
  
  // Checkpoint Summary
  getCheckpointSummary: (tenantId, data) =>
    apiClient.post('/ai/checkpoint/summary', data, {
      params: { tenant_id: tenantId }
    }),
  
  // Tier Capabilities
  getTierCapabilities: (tenantId) =>
    apiClient.get('/ai/tier/capabilities', {
      params: { tenant_id: tenantId }
    }),
  
  // ==========================================
  // Proactive AI Suggestions APIs
  // ==========================================
  
  getProactiveSuggestions: (tenantId, userId, context, entityId, entityType) =>
    apiClient.get('/ai/proactive/suggestions', {
      params: { 
        tenant_id: tenantId, 
        user_id: userId,
        context,
        entity_id: entityId,
        entity_type: entityType
      }
    }),
  
  submitSuggestionFeedback: (tenantId, userId, feedback) =>
    apiClient.post('/ai/proactive/feedback', feedback, {
      params: { tenant_id: tenantId, user_id: userId }
    }),
  
  getAIPreferences: (tenantId, userId) =>
    apiClient.get('/ai/proactive/preferences', {
      params: { tenant_id: tenantId, user_id: userId }
    }),
  
  disableSuggestionType: (tenantId, userId, suggestionType) =>
    apiClient.post('/ai/proactive/preferences/disable', null, {
      params: { tenant_id: tenantId, user_id: userId, suggestion_type: suggestionType }
    }),
  
  enableSuggestionType: (tenantId, userId, suggestionType) =>
    apiClient.post('/ai/proactive/preferences/enable', null, {
      params: { tenant_id: tenantId, user_id: userId, suggestion_type: suggestionType }
    }),
    
  // Consulting Mode APIs
  consultingDiscover: (tenantId, data) =>
    apiClient.post('/consulting/discover', data, {
      params: { tenant_id: tenantId }
    }),
  
  consultingScope: (tenantId, data) =>
    apiClient.post('/consulting/scope', data, {
      params: { tenant_id: tenantId }
    }),
  
  consultingEstimate: (tenantId, data) =>
    apiClient.post('/consulting/estimate', data, {
      params: { tenant_id: tenantId }
    }),
  
  // Proposal APIs
  generateProposal: (tenantId, data) =>
    apiClient.post('/proposal/generate', data, {
      params: { tenant_id: tenantId }
    }),
  
  getProposal: (projectId, tenantId) =>
    apiClient.get(`/proposal/${projectId}`, {
      params: { tenant_id: tenantId }
    }),
  
  // Proposal History & Management
  listProposals: (tenantId, params = {}) =>
    apiClient.get('/proposal/history', {
      params: { tenant_id: tenantId, ...params }
    }),
  
  saveProposal: (tenantId, projectId, data) =>
    apiClient.post('/proposal/save', data, {
      params: { tenant_id: tenantId, project_id: projectId }
    }),
  
  getSavedProposal: (proposalId, tenantId) =>
    apiClient.get(`/proposal/saved/${proposalId}`, {
      params: { tenant_id: tenantId }
    }),
  
  updateSavedProposal: (proposalId, tenantId, data) =>
    apiClient.patch(`/proposal/saved/${proposalId}`, data, {
      params: { tenant_id: tenantId }
    }),
  
  updateSavedProposalStatus: (proposalId, tenantId, status) =>
    apiClient.patch(`/proposal/saved/${proposalId}/status`, null, {
      params: { tenant_id: tenantId, status }
    }),
  
  // Variable Costs
  updateVariableCosts: (projectId, tenantId, variableCosts) =>
    apiClient.patch(`/proposal/${projectId}/variable-costs`, variableCosts, {
      params: { tenant_id: tenantId }
    }),
  
  // Proposal to Project Conversion (Proposal module)
  convertProposalToProjectFromProposal: (tenantId, userId, data) =>
    apiClient.post('/proposal/convert-to-project', data, {
      params: { tenant_id: tenantId, user_id: userId }
    }),
  
  updateProposalStatus: (projectId, tenantId, status) =>
    apiClient.patch(`/proposal/${projectId}/status`, { status }, {
      params: { tenant_id: tenantId }
    }),
  
  updateProposalSection: (projectId, tenantId, section, content) =>
    apiClient.patch(`/proposal/${projectId}/section`, { section, content }, {
      params: { tenant_id: tenantId }
    }),
  
  exportProposal: (projectId, tenantId, format) =>
    apiClient.post(`/proposal/${projectId}/export/${format}`, null, {
      params: { tenant_id: tenantId },
      responseType: 'blob'
    }),
  
  updateProposalEstimation: (projectId, tenantId, estimation) =>
    apiClient.patch(`/proposal/${projectId}/estimation`, estimation, {
      params: { tenant_id: tenantId }
    }),
  
  updateProposalTimeline: (projectId, tenantId, timeline) =>
    apiClient.patch(`/proposal/${projectId}/timeline`, timeline, {
      params: { tenant_id: tenantId }
    }),
  
  // Update full proposal data (for export)
  updateProposalData: (projectId, tenantId, data) =>
    apiClient.patch(`/proposal/${projectId}/full`, data, {
      params: { tenant_id: tenantId }
    }),
  
  // Rate Card Management
  getRateCard: (tenantId) =>
    apiClient.get('/consulting/rate-card', {
      params: { tenant_id: tenantId }
    }),
  
  saveRateCard: (tenantId, data, userId) =>
    apiClient.post('/consulting/rate-card', data, {
      params: { tenant_id: tenantId, user_id: userId }
    }),
  
  updateRateCardRole: (tenantId, roleId, updates) =>
    apiClient.patch(`/consulting/rate-card/${roleId}`, updates, {
      params: { tenant_id: tenantId }
    }),
  
  // Convert Proposal to Project (Consulting module)
  convertConsultingProposalToProject: (tenantId, data) =>
    apiClient.post('/consulting/convert-to-project', data, {
      params: { tenant_id: tenantId }
    }),
  
  // Consulting Suite Admin
  getConsultingStats: (tenantId) =>
    apiClient.get('/consulting/stats', {
      params: tenantId ? { tenant_id: tenantId } : {}
    }),
  
  getConsultingSuiteSettings: (tenantId) =>
    apiClient.get('/consulting/suite-settings', {
      params: { tenant_id: tenantId || localStorage.getItem('tenantId') }
    }),
  
  saveConsultingSuiteSettings: (settings) =>
    apiClient.post('/consulting/suite-settings', settings, {
      params: { tenant_id: localStorage.getItem('tenantId') }
    }),
  
  // Integrations (duplicate removed - see line 732)
  subscribeIntegrationNotify: (integrationKey, email) =>
    apiClient.post('/admin/integrations/notify', { integration_key: integrationKey, email }),
  
  // Agentic AI PM
  getAIPMState: (projectId, tenantId) =>
    apiClient.get(`/projects/${projectId}/ai-pm/state`, {
      params: { tenant_id: tenantId }
    }),
  
  enableAIPM: (projectId, tenantId, userId, options) =>
    apiClient.post(`/projects/${projectId}/ai-pm/enable`, options, {
      params: { tenant_id: tenantId, user_id: userId }
    }),
  
  disableAIPM: (projectId, tenantId, userId) =>
    apiClient.post(`/projects/${projectId}/ai-pm/disable`, {}, {
      params: { tenant_id: tenantId, user_id: userId }
    }),
  
  getAIPMInsights: (projectId, tenantId) =>
    apiClient.get(`/projects/${projectId}/ai-pm/insights`, {
      params: { tenant_id: tenantId }
    }),
  
  acceptAIPMSuggestion: (projectId, tenantId, userId, data) =>
    apiClient.post(`/projects/${projectId}/ai-pm/suggestions/accept`, data, {
      params: { tenant_id: tenantId, user_id: userId }
    }),
  
  // Individual suggestion actions (RESTful)
  approveSuggestion: (projectId, suggestionId, tenantId, userId, note = null) =>
    apiClient.post(`/projects/${projectId}/ai-pm/suggestions/${suggestionId}/approve`, 
      note ? { note } : {}, 
      { params: { tenant_id: tenantId, user_id: userId } }
    ),
  
  modifySuggestion: (projectId, suggestionId, tenantId, userId, note, fields = null) =>
    apiClient.post(`/projects/${projectId}/ai-pm/suggestions/${suggestionId}/modify`, 
      { note, fields }, 
      { params: { tenant_id: tenantId, user_id: userId } }
    ),
  
  dismissSuggestion: (projectId, suggestionId, tenantId, userId, reason) =>
    apiClient.post(`/projects/${projectId}/ai-pm/suggestions/${suggestionId}/dismiss`, 
      { reason }, 
      { params: { tenant_id: tenantId, user_id: userId } }
    ),
  
  getAIPMActivityLog: (projectId, tenantId, limit = 50) =>
    apiClient.get(`/projects/${projectId}/ai-pm/activity-log`, {
      params: { tenant_id: tenantId, limit }
    }),
  
  generateAIPMSummary: (projectId, tenantId) =>
    apiClient.post(`/projects/${projectId}/ai-pm/generate-summary`, {}, {
      params: { tenant_id: tenantId }
    }),
  
  generateAIPMWeeklyStatus: (projectId, tenantId) =>
    apiClient.post(`/projects/${projectId}/ai-pm/generate-weekly-status`, {}, {
      params: { tenant_id: tenantId }
    }),

  // ==========================================
  // Template Library APIs
  // ==========================================
  
  // Template Types
  getTemplateTypes: () =>
    apiClient.get('/templates/types'),
  
  // Global Templates (Platform Admin)
  listGlobalTemplates: (params = {}) =>
    apiClient.get('/templates/global', { params }),
  
  getGlobalTemplate: (templateId) =>
    apiClient.get(`/templates/global/${templateId}`),
  
  createGlobalTemplate: (template, userId) =>
    apiClient.post('/templates/global', template, { params: { user_id: userId } }),
  
  updateGlobalTemplate: (templateId, update, userId) =>
    apiClient.put(`/templates/global/${templateId}`, update, { params: { user_id: userId } }),
  
  deleteGlobalTemplate: (templateId, userId) =>
    apiClient.delete(`/templates/global/${templateId}`, { params: { user_id: userId } }),
  
  // Tenant Templates
  listTenantTemplates: (tenantId, params = {}) =>
    apiClient.get('/templates/tenant', { params: { tenant_id: tenantId, ...params } }),
  
  getTenantTemplate: (templateId, tenantId) =>
    apiClient.get(`/templates/tenant/${templateId}`, { params: { tenant_id: tenantId } }),
  
  createTenantTemplate: (tenantId, userId, template) =>
    apiClient.post('/templates/tenant', template, { params: { tenant_id: tenantId, user_id: userId } }),
  
  updateTenantTemplate: (templateId, tenantId, userId, update) =>
    apiClient.put(`/templates/tenant/${templateId}`, update, { params: { tenant_id: tenantId, user_id: userId } }),
  
  deleteTenantTemplate: (templateId, tenantId, userId) =>
    apiClient.delete(`/templates/tenant/${templateId}`, { params: { tenant_id: tenantId, user_id: userId } }),
  
  // Merged Templates (Primary API for UI)
  getMergedTemplates: (tenantId, params = {}) =>
    apiClient.get('/templates/merged', { params: { tenant_id: tenantId, ...params } }),
  
  getMergedTemplatesByType: (templateType, tenantId, params = {}) =>
    apiClient.get(`/templates/merged/${templateType}`, { params: { tenant_id: tenantId, ...params } }),
  
  // Clone global to tenant
  cloneGlobalToTenant: (globalTemplateId, tenantId, userId, newName = null) =>
    apiClient.post('/templates/clone-to-tenant', 
      newName ? { new_name: newName } : {},
      { params: { global_template_id: globalTemplateId, tenant_id: tenantId, user_id: userId } }
    ),
  
  // Seed default templates (Admin)
  seedDefaultTemplates: (userId) =>
    apiClient.post('/templates/seed-defaults', {}, { params: { user_id: userId } }),

  // ==========================================
  // Requirements APIs
  // ==========================================
  
  // Get requirement categories and hierarchy types
  getRequirementCategories: () =>
    apiClient.get('/requirements/categories'),
  
  // List requirements
  listRequirements: (tenantId, projectId, params = {}) =>
    apiClient.get('/requirements', { 
      params: { tenant_id: tenantId, project_id: projectId, ...params } 
    }),
  
  // Get single requirement
  getRequirement: (requirementId, tenantId) =>
    apiClient.get(`/requirements/${requirementId}`, { params: { tenant_id: tenantId } }),
  
  // Create requirement
  createRequirement: (tenantId, userId, requirement) =>
    apiClient.post('/requirements', requirement, { 
      params: { tenant_id: tenantId, user_id: userId } 
    }),
  
  // Update requirement
  updateRequirement: (requirementId, tenantId, userId, update) =>
    apiClient.put(`/requirements/${requirementId}`, update, { 
      params: { tenant_id: tenantId, user_id: userId } 
    }),
  
  // Delete requirement
  deleteRequirement: (requirementId, tenantId, userId, cascade = false) =>
    apiClient.delete(`/requirements/${requirementId}`, { 
      params: { tenant_id: tenantId, user_id: userId, cascade } 
    }),
  
  // Reorder requirements
  reorderRequirements: (tenantId, userId, items) =>
    apiClient.post('/requirements/reorder', { items }, { 
      params: { tenant_id: tenantId, user_id: userId } 
    }),
  
  // Check requirement quality
  checkRequirementQuality: (requirementId, tenantId) =>
    apiClient.get(`/requirements/${requirementId}/quality`, { 
      params: { tenant_id: tenantId } 
    }),
  
  // Get project quality summary
  getProjectQualitySummary: (projectId, tenantId) =>
    apiClient.get(`/requirements/project/${projectId}/quality-summary`, { 
      params: { tenant_id: tenantId } 
    }),
  
  // Apply template to requirement
  applyTemplateToRequirement: (requirementId, tenantId, userId, templateId, templateSource = 'global') =>
    apiClient.post(`/requirements/${requirementId}/apply-template`, {}, { 
      params: { tenant_id: tenantId, user_id: userId, template_id: templateId, template_source: templateSource } 
    }),

  // ==========================================
  // WBS & Baseline APIs
  // ==========================================
  
  // List baselines
  listBaselines: (tenantId, projectId, params = {}) =>
    apiClient.get('/wbs/baselines', { 
      params: { tenant_id: tenantId, project_id: projectId, ...params } 
    }),
  
  // Get single baseline
  getBaseline: (baselineId, tenantId) =>
    apiClient.get(`/wbs/baselines/${baselineId}`, { params: { tenant_id: tenantId } }),
  
  // Create baseline
  createBaseline: (tenantId, userId, baseline) =>
    apiClient.post('/wbs/baselines', baseline, { 
      params: { tenant_id: tenantId, user_id: userId } 
    }),
  
  // Update baseline
  updateBaseline: (baselineId, tenantId, userId, update) =>
    apiClient.put(`/wbs/baselines/${baselineId}`, update, { 
      params: { tenant_id: tenantId, user_id: userId } 
    }),
  
  // Submit baseline for approval
  submitBaseline: (baselineId, tenantId, userId, notes = null) =>
    apiClient.post(`/wbs/baselines/${baselineId}/submit`, { notes }, { 
      params: { tenant_id: tenantId, user_id: userId } 
    }),
  
  // Approve baseline
  approveBaseline: (baselineId, tenantId, userId, notes = null) =>
    apiClient.post(`/wbs/baselines/${baselineId}/approve`, { notes }, { 
      params: { tenant_id: tenantId, user_id: userId } 
    }),
  
  // Reject baseline
  rejectBaseline: (baselineId, tenantId, userId, reason) =>
    apiClient.post(`/wbs/baselines/${baselineId}/reject`, { reason }, { 
      params: { tenant_id: tenantId, user_id: userId } 
    }),
  
  // Compare baselines
  compareBaselines: (fromId, toId, tenantId) =>
    apiClient.get(`/wbs/baselines/${fromId}/compare/${toId}`, { 
      params: { tenant_id: tenantId } 
    }),
  
  // Generate WBS
  generateWBS: (tenantId, userId, request) =>
    apiClient.post('/wbs/generate', request, { 
      params: { tenant_id: tenantId, user_id: userId } 
    }),
  
  // List WBS nodes
  listWBSNodes: (tenantId, projectId, baselineId, includeTree = true) =>
    apiClient.get('/wbs/nodes', { 
      params: { tenant_id: tenantId, project_id: projectId, baseline_id: baselineId, include_tree: includeTree } 
    }),
  
  // Create WBS node
  createWBSNode: (tenantId, userId, node) =>
    apiClient.post('/wbs/nodes', node, { 
      params: { tenant_id: tenantId, user_id: userId } 
    }),
  
  // Delete WBS node
  deleteWBSNode: (nodeId, tenantId, userId, cascade = false) =>
    apiClient.delete(`/wbs/nodes/${nodeId}`, { 
      params: { tenant_id: tenantId, user_id: userId, cascade } 
    }),

  // ==========================================
  // WBS CORE API (Configurable Hierarchy)
  // ==========================================
  
  // Get WBS configuration for tenant
  getWBSConfig: () => apiClient.get('/wbs-core/config'),
  
  // Update WBS configuration for tenant
  updateWBSConfig: (config) => apiClient.put('/wbs-core/config', config),
  
  // Get WBS tree (hierarchical structure)
  getWBSCoreTree: (projectId = null) => 
    apiClient.get('/wbs-core/tree', { params: projectId ? { project_id: projectId } : {} }),
  
  // List WBS core nodes with filters
  listWBSCoreNodes: (params = {}) => 
    apiClient.get('/wbs-core/nodes', { params }),
  
  // Get single WBS core node
  getWBSCoreNode: (nodeId, includePath = true, includeChildren = false) =>
    apiClient.get(`/wbs-core/nodes/${nodeId}`, { 
      params: { include_path: includePath, include_children: includeChildren } 
    }),
  
  // Create WBS core node
  createWBSCoreNode: (node) => apiClient.post('/wbs-core/nodes', node),
  
  // Update WBS core node
  updateWBSCoreNode: (nodeId, update) => apiClient.put(`/wbs-core/nodes/${nodeId}`, update),
  
  // Delete WBS core node
  deleteWBSCoreNode: (nodeId, cascade = false) =>
    apiClient.delete(`/wbs-core/nodes/${nodeId}`, { params: { cascade } }),
  
  // Validate WBS integrity
  validateWBSCore: () => apiClient.get('/wbs-core/validate'),
  
  // Link entity to WBS node
  linkEntityToWBS: (entityType, entityId, wbsNodeId) =>
    apiClient.post(`/wbs-core/link/${entityType}/${entityId}`, null, { 
      params: { wbs_node_id: wbsNodeId } 
    }),
  
  // Unlink entity from WBS node
  unlinkEntityFromWBS: (entityType, entityId) =>
    apiClient.delete(`/wbs-core/link/${entityType}/${entityId}`),

  // ==========================================
  // WBS MIGRATION API
  // ==========================================
  
  // Get migration status
  getWBSMigrationStatus: () => apiClient.get('/wbs-core/migration/status'),
  
  // Create/get legacy bucket
  getWBSLegacyBucket: () => apiClient.post('/wbs-core/migration/legacy-bucket'),
  
  // Migrate to legacy bucket
  migrateToLegacyBucket: (dryRun = true, collection = null) => 
    apiClient.post('/wbs-core/migration/to-legacy', { dry_run: dryRun, collection }),
  
  // Validate orphan items
  validateWBSOrphans: () => apiClient.get('/wbs-core/migration/validate-orphans'),
  
  // Repair orphan items
  repairWBSOrphans: (dryRun = true) => 
    apiClient.post('/wbs-core/migration/repair-orphans', { dry_run: dryRun }),
  
  // Bulk assign to WBS
  bulkAssignToWBS: (collection, itemIds, wbsNodeId) =>
    apiClient.post('/wbs-core/migration/bulk-assign', { collection, item_ids: itemIds, wbs_node_id: wbsNodeId }),

  // ==========================================
  // AI ASSIST API
  // ==========================================
  
  // Check AI assist health
  getAIAssistHealth: () => apiClient.get('/ai-assist/health'),
  
  // Get all suggestions
  getAISuggestionsAll: (payload) => apiClient.post('/ai-assist/suggest/all', payload),
  
  // Get clarity suggestions
  getAISuggestionsClarity: (payload) => apiClient.post('/ai-assist/suggest/clarity', payload),
  
  // Get acceptance criteria suggestions
  getAISuggestionsAC: (payload) => apiClient.post('/ai-assist/suggest/acceptance-criteria', payload),
  
  // Get risk suggestions
  getAISuggestionsRisks: (payload) => apiClient.post('/ai-assist/suggest/risks', payload),
  
  // Get constraint suggestions
  getAISuggestionsConstraints: (payload) => apiClient.post('/ai-assist/suggest/constraints', payload),
  
  // Get dependency suggestions
  getAISuggestionsDependencies: (payload) => apiClient.post('/ai-assist/suggest/dependencies', payload),
  
  // Explain quality flags
  getAIQualityExplanation: (payload) => apiClient.post('/ai-assist/explain/quality-flags', payload),

  // Generic POST helper for ai-assist (renamed to avoid duplicate)
  aiAssistPost: (url, data) => apiClient.post(url.replace('/api', ''), data),

  // ==========================================
  // DOCUMENT GENERATION API
  // ==========================================
  
  // Check document service health
  getDocumentServiceHealth: () => apiClient.get('/documents/health'),
  
  // Generate Scope Pack PDF
  generateScopePack: async (payload) => {
    const response = await apiClient.post('/documents/scope-pack', payload, {
      responseType: 'blob',
    });
    return response;
  },
  
  // Generate PMO Summary PDF
  generatePMOSummary: async (payload) => {
    const response = await apiClient.post('/documents/pmo-summary', payload, {
      responseType: 'blob',
    });
    return response;
  },

  // ==========================================
  // SPEC UPLOAD & EXTRACTION API
  // ==========================================
  
  // Upload spec file and extract requirements
  uploadSpec: (formData) => apiClient.post('/scoping/upload-spec', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000, // 2 minute timeout for large files
  }),
  
  // Import extracted requirements into project
  importExtractedRequirements: (payload) => apiClient.post('/scoping/import-extracted', payload),
  
  // Get extraction history
  getExtractionHistory: (tenantId, limit = 10) => 
    apiClient.get(`/scoping/extraction-history/${tenantId}`, { params: { limit } }),
  
  // Get supported file formats
  getSupportedFormats: () => apiClient.get('/scoping/supported-formats'),

  // ==========================================
  // SCOPING WIZARD API
  // ==========================================
  
  // Create wizard session
  createWizardSession: (payload) => apiClient.post('/scoping/wizard/session', payload),
  
  // Get wizard session
  getWizardSession: (sessionId) => apiClient.get(`/scoping/wizard/session/${sessionId}`),
  
  // List wizard sessions
  listWizardSessions: (tenantId, status = null, limit = 10) => 
    apiClient.get(`/scoping/wizard/sessions/${tenantId}`, { params: { status, limit } }),
  
  // Update wizard step
  updateWizardStep: (sessionId, payload) => 
    apiClient.put(`/scoping/wizard/session/${sessionId}/step`, payload),
  
  // Get wizard steps info
  getWizardSteps: () => apiClient.get('/scoping/wizard/steps'),

  // ==========================================
  // PROJECT FROM BASELINE API
  // ==========================================
  
  // Create delivery project from approved baseline
  createProjectFromBaseline: (baselineId, tenantId, userId, projectName = null) => 
    apiClient.post(`/projects/create-from-baseline/${baselineId}`, null, {
      params: { tenant_id: tenantId, user_id: userId, project_name: projectName },
    }),

  // ==========================================
  // RESOURCE MANAGEMENT API (P4)
  // ==========================================
  
  // Roles API
  createRole: (data) => apiClient.post('/resource/roles', data),
  listRoles: (tenantId, includeUtilisation = false) => 
    apiClient.get('/resource/roles', { params: { tenant_id: tenantId, include_utilisation: includeUtilisation } }),
  getRole: (roleId, tenantId) => 
    apiClient.get(`/resource/roles/${roleId}`, { params: { tenant_id: tenantId } }),
  updateRole: (roleId, tenantId, data) => 
    apiClient.patch(`/resource/roles/${roleId}`, data, { params: { tenant_id: tenantId } }),
  deleteRole: (roleId, tenantId) => 
    apiClient.delete(`/resource/roles/${roleId}`, { params: { tenant_id: tenantId } }),
  
  // Resource Teams API
  createResourceTeam: (data) => apiClient.post('/resource/teams', data),
  listResourceTeams: (tenantId, includeUtilisation = false) => 
    apiClient.get('/resource/teams', { params: { tenant_id: tenantId, include_utilisation: includeUtilisation } }),
  getResourceTeam: (teamId, tenantId) => 
    apiClient.get(`/resource/teams/${teamId}`, { params: { tenant_id: tenantId } }),
  updateResourceTeam: (teamId, tenantId, data) => 
    apiClient.patch(`/resource/teams/${teamId}`, data, { params: { tenant_id: tenantId } }),
  deleteResourceTeam: (teamId, tenantId) => 
    apiClient.delete(`/resource/teams/${teamId}`, { params: { tenant_id: tenantId } }),
  
  // Capacity Calendar API
  getCapacityCalendar: (entityType, entityId, tenantId) => 
    apiClient.get(`/resource/capacity/${entityType}/${entityId}`, { params: { tenant_id: tenantId } }),
  updateCapacityCalendar: (entityType, entityId, tenantId, data) => 
    apiClient.post(`/resource/capacity/${entityType}/${entityId}`, data, { params: { tenant_id: tenantId } }),
  getCapacitySummary: (tenantId, yearMonth = null) => 
    apiClient.get('/resource/capacity/summary', { params: { tenant_id: tenantId, year_month: yearMonth } }),
  
  // Allocations API
  createAllocation: (data) => apiClient.post('/resource/allocations', data),
  listAllocations: (tenantId, filters = {}) => 
    apiClient.get('/resource/allocations', { params: { tenant_id: tenantId, ...filters } }),
  getAllocation: (allocationId, tenantId) => 
    apiClient.get(`/resource/allocations/${allocationId}`, { params: { tenant_id: tenantId } }),
  updateAllocation: (allocationId, tenantId, data) => 
    apiClient.patch(`/resource/allocations/${allocationId}`, data, { params: { tenant_id: tenantId } }),
  deleteAllocation: (allocationId, tenantId) => 
    apiClient.delete(`/resource/allocations/${allocationId}`, { params: { tenant_id: tenantId } }),
  
  // Demand Calculation API
  recalculateDemand: (baselineId, tenantId) => 
    apiClient.post(`/resource/demand/recalculate/${baselineId}`, null, { params: { tenant_id: tenantId } }),
  getDemandSnapshot: (baselineId, tenantId) => 
    apiClient.get(`/resource/demand/${baselineId}`, { params: { tenant_id: tenantId } }),
  
  // Utilisation Heatmap API
  getUtilisationHeatmap: (tenantId, startMonth = null, endMonth = null, entityType = null) => 
    apiClient.get('/resource/utilisation/heatmap', { 
      params: { tenant_id: tenantId, start_month: startMonth, end_month: endMonth, entity_type: entityType } 
    }),
  
  // Portfolio Capacity API
  getPortfolioCapacity: (tenantId, yearMonth = null) => 
    apiClient.get('/resource/portfolio/capacity', { params: { tenant_id: tenantId, year_month: yearMonth } }),

  // ==========================================
  // TENANT SETTINGS API (P4.1)
  // ==========================================
  
  // Get supported currencies
  listCurrencies: () => apiClient.get('/tenant/currencies'),
  
  // Get tenant settings
  getTenantSettings: (tenantId) => 
    apiClient.get('/tenant/settings', { params: { tenant_id: tenantId } }),
  
  // Update tenant settings
  updateTenantSettings: (tenantId, data) => 
    apiClient.put('/tenant/settings', data, { params: { tenant_id: tenantId } }),
  
  // Get tenant info with stats
  getTenantInfo: (tenantId) => 
    apiClient.get('/tenant/info', { params: { tenant_id: tenantId } }),

  // ==========================================
  // TEAM MEMBERS API (P4.2)
  // ==========================================
  
  // List team members
  listTeamMembers: (teamId, tenantId) => 
    apiClient.get(`/resource/teams/${teamId}/members`, { params: { tenant_id: tenantId } }),
  
  // Add team member
  addTeamMember: (teamId, tenantId, data) => 
    apiClient.post(`/resource/teams/${teamId}/members`, data, { params: { tenant_id: tenantId } }),
  
  // Update team member
  updateTeamMember: (memberId, teamId, tenantId, data) => 
    apiClient.patch(`/resource/team-members/${memberId}`, data, { params: { team_id: teamId, tenant_id: tenantId } }),
  
  // Remove team member
  removeTeamMember: (memberId, teamId, tenantId) => 
    apiClient.delete(`/resource/team-members/${memberId}`, { params: { team_id: teamId, tenant_id: tenantId } }),

  // ==========================================
  // CONFLICT DETECTION API (P4.2)
  // ==========================================
  
  // Get resource conflicts
  getResourceConflicts: (tenantId, startMonth = null, endMonth = null) => 
    apiClient.get('/resource/conflicts', { params: { tenant_id: tenantId, start_month: startMonth, end_month: endMonth } }),

  // ==========================================
  // AI RESOURCE ADVISOR (P5)
  // ==========================================
  
  // Get AI resource suggestions
  getResourceAISuggestions: ({ tenantId, selectedEntityType, selectedEntityId, selectedMonth, analysisFocus = 'all' }) => 
    apiClient.post('/ai-assist/resource-setup', {
      tenant_id: tenantId,
      selected_entity_type: selectedEntityType,
      selected_entity_id: selectedEntityId,
      selected_month: selectedMonth,
      analysis_focus: analysisFocus
    }),
  
  // Get rebalancing suggestions for a specific conflict
  getRebalanceSuggestion: ({ tenantId, entityType, entityId, month }) => 
    apiClient.post('/ai-assist/resource-setup/rebalance', null, { 
      params: { tenant_id: tenantId, entity_type: entityType, entity_id: entityId, month } 
    }),
  
  // Apply AI suggestion
  applyResourceAISuggestion: ({ tenantId, suggestionId, historyId, userId = 'system' }) => 
    apiClient.post('/ai-assist/resource-setup/apply', null, { 
      params: { tenant_id: tenantId, suggestion_id: suggestionId, history_id: historyId, user_id: userId } 
    }),
  
  // Get AI history
  getResourceAIHistory: (tenantId, limit = 20) => 
    apiClient.get('/ai-assist/resource-setup/history', { params: { tenant_id: tenantId, limit } }),

  // ==========================================
  // ANALYTICS Q&A API (Sprint 18)
  // ==========================================
  
  // Natural language analytics query
  analyticsQA: (params) => apiClient.post('/analytics/qa', params),
  
  // Log telemetry event (no PII)
  logTelemetry: (event) => apiClient.post('/telemetry/event', event),

  // ==========================================
  // NOTIFICATION PREFERENCES API (Sprint 19 - Settings Module)
  // ==========================================
  
  // Get notification preferences (Settings module - general user prefs)
  getSettingsNotificationPreferences: () => apiClient.get('/settings/notifications'),
  
  // Update notification preferences (Settings module)
  updateSettingsNotificationPreferences: (prefs) => apiClient.put('/settings/notifications', prefs),
  
  // Get queued notifications (offline fallback)
  getQueuedNotifications: () => apiClient.get('/settings/notifications/outbox'),

  // ==========================================
  // SAVED VIEWS API (Sprint 19)
  // ==========================================
  
  // Get saved views
  getSavedViews: (includeDefaults = true) => apiClient.get('/saved-views', { params: { include_defaults: includeDefaults } }),
  
  // Create saved view
  createSavedView: (view) => apiClient.post('/saved-views', view),
  
  // Update saved view (pin/unpin)
  updateSavedView: (viewId, updates) => apiClient.put(`/saved-views/${viewId}`, updates),
  
  // Delete saved view
  deleteSavedView: (viewId) => apiClient.delete(`/saved-views/${viewId}`),
  
  // Create default views
  createDefaultViews: () => apiClient.post('/saved-views/defaults'),

  // ==========================================
  // SLA CONFIGURATION API (Sprint 19)
  // ==========================================
  
  // Get SLA configuration
  getSLAConfig: () => apiClient.get('/settings/sla'),
  
  // Update SLA thresholds
  updateSLAConfig: (config) => apiClient.put('/settings/sla', config),
  
  // Get SLA status
  getSLAStatus: () => apiClient.get('/settings/sla/status'),

  // ==========================================
  // PLATFORM ADMIN - TENANT MANAGEMENT API
  // ==========================================
  
  // List all tenants (platform admin only)
  listTenants: (params = {}) => apiClient.get('/billing/tenants', { params }),
  
  // Get single tenant details
  getTenant: (tenantId) => apiClient.get(`/billing/tenant/${tenantId}`),
  
  // Create new tenant (duplicate removed - see line 764)
  
  // Clone existing tenant
  cloneTenant: (data) => apiClient.post('/billing/tenants/clone', data),
  
  // Update tenant
  updateTenant: (tenantId, data) => apiClient.patch(`/billing/tenant/${tenantId}`, data),
  
  // Delete tenant
  deleteTenant: (tenantId) => apiClient.delete(`/billing/tenant/${tenantId}`),
  
  // Get tenant billing details (extended)
  getTenantBillingDetails: (tenantId) => apiClient.get(`/billing/tenant/${tenantId}/billing-details`),
  
  // Get tenant users (platform admin)
  getTenantUsers: (tenantId) => apiClient.get(`/users`, { params: { tenant_id: tenantId } }),
  
  // Get all users across tenants (platform admin)
  getAllPlatformUsers: (params = {}) => apiClient.get('/users/all', { params }),
  
  // Get tenant configuration (features + security)
  getTenantConfig: (tenantId) => apiClient.get(`/billing/tenant/${tenantId}/config`),
  
  // Update tenant configuration
  updateTenantConfig: (tenantId, data) => apiClient.patch(`/billing/tenant/${tenantId}/config`, data),
  
  // Platform-wide user management
  getPlatformUsers: (params = {}) => apiClient.get('/billing/platform/users', { params }),
  impersonateUser: (userId) => apiClient.post(`/billing/platform/users/${userId}/impersonate`),
  suspendPlatformUser: (userId) => apiClient.post(`/billing/platform/users/${userId}/suspend`),
  activatePlatformUser: (userId) => apiClient.post(`/billing/platform/users/${userId}/activate`),

  // ==========================================
  // P1-001: DECISION LEDGER APIs
  // ==========================================
  
  // List decisions with filters
  getDecisionLedger: (params = {}) => apiClient.get('/decision-ledger', { params }),
  
  // Get single decision
  getDecisionById: (decisionId) => apiClient.get(`/decision-ledger/${decisionId}`),
  
  // Verify decision integrity
  verifyDecisionIntegrity: (decisionId) => apiClient.get(`/decision-ledger/${decisionId}/verify`),
  
  // Get decision types
  getDecisionTypes: () => apiClient.get('/decision-ledger/types'),
  
  // Get decision statistics
  getDecisionStatistics: (tenantId = null) => 
    apiClient.get('/decision-ledger/statistics', { params: tenantId ? { tenant_id: tenantId } : {} }),
  
  // Record a decision (human)
  recordDecision: (data) => apiClient.post('/decision-ledger', data),
  
  // Record system decision
  recordSystemDecision: (data) => apiClient.post('/decision-ledger/system', data),
  
  // Get decisions for an entity
  getEntityDecisions: (entityType, entityId, includeLinked = true) => 
    apiClient.get(`/decision-ledger/entity/${entityType}/${entityId}`, { params: { include_linked: includeLinked } }),
  
  // Get decision chain for an entity
  getDecisionChain: (entityType, entityId) => 
    apiClient.get(`/decision-ledger/entity/${entityType}/${entityId}/chain`),
  
  // Get entity integrity score
  getEntityIntegrityScore: (entityType, entityId) => 
    apiClient.get(`/decision-ledger/entity/${entityType}/${entityId}/integrity-score`),
  
  // Record decision outcome
  recordDecisionOutcome: (decisionId, data) => 
    apiClient.post(`/decision-ledger/${decisionId}/outcome`, data),
  
  // Supersede a decision
  supersedeDecision: (decisionId, newDecisionId) => 
    apiClient.post(`/decision-ledger/${decisionId}/supersede`, { new_decision_id: newDecisionId }),
  
  // Audit queries
  auditWhoApproved: (entityType, entityId) => 
    apiClient.get(`/decision-ledger/audit/who-approved/${entityType}/${entityId}`),
  
  auditWhatChanged: (entityType, entityId) => 
    apiClient.get(`/decision-ledger/audit/what-changed/${entityType}/${entityId}`),
  
  auditTimeline: (entityType, entityId) => 
    apiClient.get(`/decision-ledger/audit/timeline/${entityType}/${entityId}`),

  // Generic request helpers (Sprint 20)
  get: (url, config) => apiClient.get(url.replace('/api', ''), config),
  post: (url, data, config) => apiClient.post(url.replace('/api', ''), data, config),
  put: (url, data) => apiClient.put(url.replace('/api', ''), data),
  delete: (url, config) => apiClient.delete(url.replace('/api', ''), config),

  // ==========================================
  // EXECUTIVE HUB V1 COMPLETION APIs
  // ==========================================

  // Resource Intelligence for Executive Hub
  getResourceExecutiveDashboard: () => apiClient.get('/resources/dashboard/executive'),
  getResourceBottlenecks: () => apiClient.get('/resources/capacity/bottlenecks'),
  getResourceSummary: () => apiClient.get('/resources/summary'),
  
  // Financial Intelligence for Executive Hub
  getFinancialVariances: (portfolioId = null) => 
    apiClient.get('/financial/variances', { params: portfolioId ? { portfolio_id: portfolioId } : {} }),
  getFinancialSummary: () => apiClient.get('/financial/estimate'),
  
  // Benefits/Outcome Intelligence for Executive Hub
  getBenefitsExecutiveDashboard: () => apiClient.get('/benefits/dashboard/executive'),
  getBenefitsSummary: () => apiClient.get('/benefits/summary'),
  getBenefitsAtRisk: () => apiClient.get('/benefits/at-risk'),
  getOutcomeScore: () => apiClient.get('/benefits/outcome-score'),
  
  // User Preferences (for wizard persistence) - auth-based
  getAuthUserPreferences: () => apiClient.get('/auth/preferences'),
  updateAuthUserPreferences: (preferences) => apiClient.patch('/auth/preferences', preferences),
};

export default apiClient;
// api is already exported as a named export on line 38
